import backgroundOptionsData from "/Users/michaeltucker/Documents/coding-projects/client/src/BackgroundOptionsData.js";
import {
  getDefaultBackgroundImageSettings,
  isImageBackgroundValue,
  isPatternBackgroundValue,
  normalizeBackgroundImageSettings,
} from "utils/classStyleBackgroundImage";
import { isGradientBackground } from "utils/gradientBackground";
import { formatSidebarPeriodLabel } from "utils/sidebarTodaysClasses";

export const CLASS_STYLE_FONT_SIZE_OPTIONS = [
  "16pt",
  "18pt",
  "20pt",
  "24pt",
  "30pt",
  "36pt",
  "48pt",
];

export const CLASS_STYLE_BACKGROUND_PRESETS = backgroundOptionsData
  .slice(0, 9)
  .map((option, index) => ({
    id: `background-preset-${index}`,
    thumbnail: option.thumbnail,
    value: option.image ? `url(${option.image})` : "",
  }));

export const CLASS_STYLE_SHADOW_PRESETS = [
  {
    id: "none",
    label: "None",
    boxShadow: "none",
  },
  {
    id: "soft",
    label: "Soft",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.25)",
  },
  {
    id: "raised",
    label: "Raised",
    boxShadow: "0 2px 5px rgba(15, 23, 42, 0.4)", // 0 16px 32px rgba(15, 23, 42, 0.18)
  },
  {
    id: "deep",
    label: "Deep",
    boxShadow: "0 20px 40px rgba(15, 23, 42, 0.7)",
  },
  {
    id: "dramatic",
    label: "Dramatic",
    boxShadow: "0 24px 48px rgba(2, 6, 23, 0.3)",
  },
  {
    id: "glow",
    label: "Glow",
    boxShadow: "0 0 0 1px rgba(148, 163, 184, 0.2), 0 18px 36px rgba(59, 130, 246, 0.18)",
  },
  {
    id: "spotlight",
    label: "Spotlight",
    boxShadow: "0 0 0 1px rgba(96, 165, 250, 0.22), 0 22px 44px rgba(37, 99, 235, 0.28)",
  },
  {
    id: "hard",
    label: "Hard",
    boxShadow: "4px 4px 0 1px rgba(0, 0, 0, 0.52)",
  },
  {
    id: "custom",
    label: "Custom",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.24)",
  },
];

export const CONTENT_BOX_STYLE_PRESET_IDS = ["content-box-preset-1", "content-box-preset-2", "content-box-preset-3", "content-box-preset-4"];

export const CONTENT_BOX_STYLE_PRESET_OPTIONS = CONTENT_BOX_STYLE_PRESET_IDS.map((id, index) => ({
  id,
  label: `Box Preset ${index + 1}`,
}));

const PRESET_COMPLEMENT_TOKENS = {
  "lesson-launchpad-default": { family: "neutral", accent: "#004aad", accentText: "#ffffff", soft: "#eff6ff", softText: "#0f172a", muted: "#64748b" },
  "scholar-sky": { family: "blue", accent: "#2563eb", accentText: "#ffffff", soft: "#eff6ff", softText: "#0f172a", muted: "#475569" },
  "midnight-studio": { family: "dark", accent: "#22d3ee", accentText: "#020617", soft: "#164e63", softText: "#e0f2fe", muted: "#94a3b8" },
  "matrix-terminal": { family: "dark", accent: "#39ff14", accentText: "#000000", soft: "#0a0f0a", softText: "#39ff14", muted: "#22c55e" },
  "warm-bulletin": { family: "warm", accent: "#b45309", accentText: "#fff7ed", soft: "#ffedd5", softText: "#4a2f1f", muted: "#9a3412" },
  "pep-rally": { family: "playful", accent: "#f97316", accentText: "#ffffff", soft: "#ffedd5", softText: "#9a3412", muted: "#c2410c" },
  "forest-study-hall": { family: "green", accent: "#2d6a4f", accentText: "#ecfdf3", soft: "#dcfce7", softText: "#1b4332", muted: "#40916c" },
  "sunrise-seminar": { family: "warm", accent: "#ea580c", accentText: "#fff7ed", soft: "#fed7aa", softText: "#7c2d12", muted: "#c2410c" },
  "aurora-lab": { family: "blue", accent: "#0284c7", accentText: "#ffffff", soft: "#e0f2fe", softText: "#075985", muted: "#38bdf8" },
  "chalkboard-classic": { family: "dark", accent: "#9fe3ba", accentText: "#13271f", soft: "#244537", softText: "#dcfce7", muted: "#86efac" },
  "neon-night-shift": { family: "dark", accent: "#22d3ee", accentText: "#020617", soft: "#164e63", softText: "#e0f2fe", muted: "#67e8f9" },
  "lavender-focus": { family: "purple", accent: "#7c3aed", accentText: "#ffffff", soft: "#ede9fe", softText: "#4c1d95", muted: "#a78bfa" },
  "retro-poster": { family: "playful", accent: "#f97316", accentText: "#fff7d6", soft: "#ffedd5", softText: "#7c2d12", muted: "#d97706" },
  "minimal-slate": { family: "neutral", accent: "#334155", accentText: "#ffffff", soft: "#e2e8f0", softText: "#111827", muted: "#64748b" },
  "ocean-depth": { family: "dark", accent: "#38bdf8", accentText: "#071a2c", soft: "#0f3f61", softText: "#e0f2fe", muted: "#7dd3fc" },
  "sunset-coral": { family: "warm", accent: "#e11d48", accentText: "#fff1f2", soft: "#ffe4e6", softText: "#7f1d1d", muted: "#fb7185" },
  "royal-lecture": { family: "dark", accent: "#fbbf24", accentText: "#0f172a", soft: "#334155", softText: "#fde68a", muted: "#d97706" },
  "graph-paper-lab": { family: "blue", accent: "#2563eb", accentText: "#ffffff", soft: "#dbeafe", softText: "#1e3a8a", muted: "#60a5fa" },
  "mint-notebook": { family: "green", accent: "#059669", accentText: "#ecfdf3", soft: "#dcfce7", softText: "#0f3d2e", muted: "#34d399" },
  "steel-blue-studio": { family: "neutral", accent: "#475569", accentText: "#ffffff", soft: "#e2e8f0", softText: "#1e293b", muted: "#64748b" },
  "citrus-pop": { family: "playful", accent: "#65a30d", accentText: "#fefce8", soft: "#ecfccb", softText: "#365314", muted: "#84cc16" },
  "plum-academy": { family: "dark", accent: "#c084fc", accentText: "#2e143b", soft: "#55306b", softText: "#f3e8ff", muted: "#d8b4fe" },
  "sunny-homeroom": { family: "warm", accent: "#f59e0b", accentText: "#451a03", soft: "#fef3c7", softText: "#713f12", muted: "#fbbf24" },
  "night-ops": { family: "dark", accent: "#22d3ee", accentText: "#020617", soft: "#1e293b", softText: "#bae6fd", muted: "#67e8f9" },
  "rose-gold": { family: "warm", accent: "#db2777", accentText: "#fff1f2", soft: "#fce7f3", softText: "#7a2846", muted: "#f472b6" },
  "indigo-workshop": { family: "dark", accent: "#818cf8", accentText: "#172554", soft: "#312e81", softText: "#e0e7ff", muted: "#a5b4fc" },
  "strawberry-fields": { family: "playful", accent: "#ef4444", accentText: "#fff7ed", soft: "#ffe4e6", softText: "#7f1d1d", muted: "#ffdb49" },
  "midnight-glow": { family: "dark", accent: "#c7d2fe", accentText: "#00114b", soft: "#2d3b79", softText: "#e0e7ff", muted: "#ffffff" },
  "retro-metro": { family: "playful", accent: "#FB2E01", accentText: "#FFFEB3", soft: "#6FCB9F", softText: "#2f4f3d", muted: "#666547" },
  "comic-classroom": { family: "playful", accent: "#ef4444", accentText: "#fff7ed", soft: "#facc15", softText: "#1e293b", muted: "#111827" },
  "arcade-after-school": { family: "dark", accent: "#ff2bd6", accentText: "#16001f", soft: "#1f1147", softText: "#f5d0fe", muted: "#00e5ff" },
  "terrazzo-tuesday": { family: "playful", accent: "#ff6b6b", accentText: "#fffaf0", soft: "#7dd3fc", softText: "#214761", muted: "#1f2937" },
  "desert-workshop": { family: "warm", accent: "#c2410c", accentText: "#fff7ed", soft: "#fed7aa", softText: "#78350f", muted: "#92400e" },
  "poolside-planner": { family: "blue", accent: "#0891b2", accentText: "#ecfeff", soft: "#ccfbf1", softText: "#134e4a", muted: "#0f766e" },
  "berry-binder": { family: "purple", accent: "#be185d", accentText: "#fff1f2", soft: "#fbcfe8", softText: "#831843", muted: "#9d174d" },
  "traffic-light-lab": { family: "playful", accent: "#dc2626", accentText: "#fff7ed", soft: "#facc15", softText: "#365314", muted: "#16a34a" },
  "blueprint-bold": { family: "blue", accent: "#f59e0b", accentText: "#172554", soft: "#1d4ed8", softText: "#dbeafe", muted: "#93c5fd" },
  "paper-cutouts": { family: "playful", accent: "#2563eb", accentText: "#eff6ff", soft: "#fef3c7", softText: "#334155", muted: "#f97316" },
  "jungle-gym": { family: "green", accent: "#f97316", accentText: "#fff7ed", soft: "#bbf7d0", softText: "#14532d", muted: "#15803d" },
  "space-cadet": { family: "dark", accent: "#facc15", accentText: "#111827", soft: "#312e81", softText: "#e0e7ff", muted: "#a78bfa" },
  "bubblegum-board": { family: "playful", accent: "#ec4899", accentText: "#fff1f2", soft: "#fbcfe8", softText: "#831843", muted: "#8b5cf6" },
  "marker-box": { family: "neutral", accent: "#2563eb", accentText: "#ffffff", soft: "#f8fafc", softText: "#0f172a", muted: "#ef4444" },
  "limeade-lecture": { family: "playful", accent: "#65a30d", accentText: "#f7fee7", soft: "#d9f99d", softText: "#365314", muted: "#0f766e" },
  "clay-table": { family: "warm", accent: "#a16207", accentText: "#fff7ed", soft: "#fde68a", softText: "#713f12", muted: "#7c2d12" },
  "rainy-day-reading": { family: "blue", accent: "#0369a1", accentText: "#f0f9ff", soft: "#bae6fd", softText: "#0c4a6e", muted: "#64748b" },
  "electric-peach": { family: "warm", accent: "#f97316", accentText: "#fff7ed", soft: "#fed7aa", softText: "#7c2d12", muted: "#db2777" },
  "primary-palette": { family: "playful", accent: "#1d4ed8", accentText: "#eff6ff", soft: "#fde047", softText: "#172554", muted: "#dc2626" },
  "book-fair": { family: "warm", accent: "#7c3aed", accentText: "#f5f3ff", soft: "#fef3c7", softText: "#4a2f1f", muted: "#d97706" },
  "chalk-pop": { family: "dark", accent: "#166534", accentText: "#dcfce7", soft: "#14532d", softText: "#dcfce7", muted: "#fde68a" },
  "racecar-red": { family: "playful", accent: "#ff1f1f", accentText: "#ffffff", soft: "#fff1f1", softText: "#111827", muted: "#111827" },
  "grand-canyon": { family: "warm", accent: "#c2410c", accentText: "#fff7ed", soft: "#fed7aa", softText: "#7c2d12", muted: "#92400e" },
  "clear-sky-glass": { family: "blue", accent: "#1d4ed8", accentText: "#ffffff", soft: "#dbeafe", softText: "#1e3a8a", muted: "#2563eb" },
  "midnight-glass": { family: "dark", accent: "#38bdf8", accentText: "#0f172a", soft: "#1e293b", softText: "#e2e8f0", muted: "#7dd3fc" },
  "golden-hour-glass": { family: "warm", accent: "#c2410c", accentText: "#fff7ed", soft: "#ffedd5", softText: "#7c2d12", muted: "#ea580c" },
  "emerald-mist-glass": { family: "green", accent: "#047857", accentText: "#ecfdf5", soft: "#d1fae5", softText: "#064e3b", muted: "#059669" },
};

const buildContentBoxStylePreset = (id, label, values) => ({
  id,
  label,
  defaultFontStyle: values.font,
  defaultFontSize: values.size || "20pt",
  defaultFontColor: values.text,
  cardBackgroundColor: values.background,
  cardBackgroundOpacity: values.opacity ?? 1,
  contentSurfaceShadow: values.shadow,
  contentSurfaceShadowCustom: values.shadowCustom,
  contentSurfaceOutlineWidth: values.outlineWidth,
  contentSurfaceOutlineColor: values.outlineColor,
  contentSurfaceOutlineStyle: values.outlineStyle,
  contentSurfaceCornerRadius: values.cornerRadius,
});

const buildPresetContentBoxPresets = (base, tokens) => {
  const shadowCustom = base.surfaceShadowCustom;
  const baseCornerRadius = base.surfaceCornerRadius ?? 16;
  const varyCornerRadius = (cornerRadius) => (baseCornerRadius === 0 ? 0 : cornerRadius);
  return [
    buildContentBoxStylePreset(CONTENT_BOX_STYLE_PRESET_IDS[0], "Box Preset 1", {
      font: base.defaultFontStyle,
      size: base.defaultFontSize || "20pt",
      text: base.defaultFontColor,
      background: base.cardBackgroundColor,
      opacity: base.cardBackgroundOpacity ?? 1,
      shadow: base.contentSurfaceShadow || base.surfaceShadow || "none",
      shadowCustom,
      outlineWidth: base.contentSurfaceOutlineWidth ?? base.surfaceOutlineWidth ?? 0,
      outlineColor: base.contentSurfaceOutlineColor || base.surfaceOutlineColor || tokens.muted,
      outlineStyle: base.contentSurfaceOutlineStyle || base.surfaceOutlineStyle || "solid",
      cornerRadius: base.contentSurfaceCornerRadius ?? base.surfaceCornerRadius ?? 16,
    }),
    buildContentBoxStylePreset(CONTENT_BOX_STYLE_PRESET_IDS[1], "Box Preset 2", {
      font: base.defaultFontStyle,
      size: "24pt",
      text: tokens.accentText,
      background: tokens.accent,
      opacity: 1,
      shadow: base.contentSurfaceShadow || base.surfaceShadow || "soft",
      shadowCustom,
      outlineWidth: 0,
      outlineColor: tokens.accent,
      outlineStyle: "solid",
      cornerRadius: varyCornerRadius(Math.min(28, baseCornerRadius + 4)),
    }),
    buildContentBoxStylePreset(CONTENT_BOX_STYLE_PRESET_IDS[2], "Box Preset 3", {
      font: base.defaultFontStyle,
      size: "18pt",
      text: tokens.softText,
      background: tokens.soft,
      opacity: tokens.family === "dark" ? 0.88 : 0.92,
      shadow: "soft",
      outlineWidth: 1,
      outlineColor: tokens.muted,
      outlineStyle: "solid",
      cornerRadius: varyCornerRadius(Math.min(32, baseCornerRadius + 8)),
    }),
    buildContentBoxStylePreset(CONTENT_BOX_STYLE_PRESET_IDS[3], "Box Preset 4", {
      font: base.defaultFontStyle,
      size: "20pt",
      text: base.defaultFontColor,
      background: base.cardBackgroundColor,
      opacity: 1,
      shadow: "none",
      outlineWidth: Math.max(2, base.surfaceOutlineWidth || 2),
      outlineColor: tokens.accent,
      outlineStyle: base.surfaceOutlineStyle || "solid",
      cornerRadius: varyCornerRadius(Math.max(0, baseCornerRadius - 2)),
    }),
  ];
};

const buildCompletePresetStyleDefaults = (presetId, styleDefaults) => {
  const base = { ...styleDefaults };
  const tokens = PRESET_COMPLEMENT_TOKENS[presetId] || PRESET_COMPLEMENT_TOKENS["lesson-launchpad-default"];
  const isDark = tokens.family === "dark";
  const agendaBackground = isDark ? tokens.soft : base.cardBackgroundColor;
  const reminderBackground = isDark ? base.reminderBackgroundColor : (base.reminderBackgroundColor || tokens.soft);
  const countdownBackground = isDark ? base.countdownBackgroundColor : (base.countdownBackgroundColor || tokens.soft);

  return {
    ...base,
    defaultContentBoxStylePresetId: base.defaultContentBoxStylePresetId || CONTENT_BOX_STYLE_PRESET_IDS[0],
    cardBackgroundOpacity: base.cardBackgroundOpacity ?? 1,
    backgroundAnnouncementsOpacity: base.backgroundAnnouncementsOpacity ?? (isDark ? 0.94 : 1),
    backgroundClockOpacity: base.backgroundClockOpacity ?? (isDark ? 0.94 : 1),
    contentBoxStylePresets: base.contentBoxStylePresets || buildPresetContentBoxPresets(base, tokens),
    agendaBackgroundColor: base.agendaBackgroundColor || agendaBackground,
    agendaBackgroundOpacity: base.agendaBackgroundOpacity ?? (isDark ? 0.92 : 1),
    agendaTitleFont: base.agendaTitleFont || base.defaultFontStyle,
    agendaTitleColor: base.agendaTitleColor || base.defaultFontColor,
    agendaStepFont: base.agendaStepFont || base.defaultFontStyle,
    agendaStepColor: base.agendaStepColor || base.defaultFontColor,
    agendaSubStepFont: base.agendaSubStepFont || base.defaultFontStyle,
    agendaSubStepColor: base.agendaSubStepColor || tokens.muted,
    agendaActiveStepBackgroundColor: base.agendaActiveStepBackgroundColor || tokens.accent,
    agendaActiveStepColor: base.agendaActiveStepColor || tokens.accentText,
    agendaActiveSubStepColor: base.agendaActiveSubStepColor || tokens.accentText,
    agendaProgressTrackColor: base.agendaProgressTrackColor || (isDark ? "rgba(255, 255, 255, 0.22)" : "rgba(15, 23, 42, 0.14)"),
    agendaProgressFillColor: base.agendaProgressFillColor || tokens.progress || tokens.accent,
    agendaProgressScrubberColor: base.agendaProgressScrubberColor || tokens.accentText,
    reminderBackgroundColor: reminderBackground,
    reminderFontStyle: base.reminderFontStyle || base.defaultFontStyle,
    reminderFontColor: base.reminderFontColor || base.defaultFontColor,
    countdownBackgroundColor: countdownBackground,
    countdownMessageFontStyle: base.countdownMessageFontStyle || base.countdownFontStyle || base.defaultFontStyle,
    countdownMessageFontColor: base.countdownMessageFontColor || base.countdownFontColor || base.defaultFontColor,
    countdownFontStyle: base.countdownFontStyle || base.defaultFontStyle,
    countdownFontColor: base.countdownFontColor || tokens.accent,
    marqueeSurfaceCornerRadius: base.marqueeSurfaceCornerRadius ?? base.surfaceCornerRadius ?? 16,
    clockSurfaceCornerRadius: base.clockSurfaceCornerRadius ?? base.surfaceCornerRadius ?? 16,
    agendaSurfaceShadow: base.agendaSurfaceShadow || base.surfaceShadow,
    reminderSurfaceShadow: base.reminderSurfaceShadow || base.surfaceShadow,
    countdownSurfaceShadow: base.countdownSurfaceShadow || base.surfaceShadow,
  };
};

const TRANSPARENT_CONTENT_BOX_PRESET_SIZES = ["20pt", "24pt", "18pt", "20pt"];

const buildTransparentContentBoxStylePresets = (base = {}) => {
  const font = base.defaultFontStyle || "Poppins, sans-serif";
  const text = base.defaultFontColor || "#0f172a";
  const radius = base.surfaceCornerRadius ?? 16;

  return CONTENT_BOX_STYLE_PRESET_IDS.map((id, index) => ({
    id,
    label: `Box Preset ${index + 1}`,
    defaultFontStyle: font,
    defaultFontSize: TRANSPARENT_CONTENT_BOX_PRESET_SIZES[index],
    defaultFontColor: text,
    cardBackgroundColor: "transparent",
    cardBackgroundOpacity: 0,
    contentSurfaceShadow: "none",
    contentSurfaceOutlineWidth: 0,
    contentSurfaceCornerRadius: radius,
  }));
};

const buildGlassPresetStyleDefaults = (overrides = {}) => {
  const {
    background,
    defaultFontColor,
    defaultFontStyle = "Poppins, sans-serif",
    clockFontColor = defaultFontColor,
    classInfoFontColor = clockFontColor,
    reminderBackgroundColor,
    reminderFontColor,
    countdownBackgroundColor,
    countdownFontColor,
    agendaActiveStepBackgroundColor,
    agendaActiveStepColor,
    agendaSubStepColor,
    agendaProgressTrackColor,
    agendaProgressFillColor,
    reminderOutlineColor = reminderFontColor,
    countdownOutlineColor = countdownFontColor,
    reminderOutlineWidth = 2,
    countdownOutlineWidth = 2,
    surfaceCornerRadius = 16,
  } = overrides;

  const base = {
    defaultFontStyle,
    defaultFontColor,
    cardBackgroundColor: "transparent",
    cardBackgroundOpacity: 0,
    background,
    backgroundAnnouncements: "transparent",
    backgroundAnnouncementsOpacity: 0,
    backgroundClock: "transparent",
    backgroundClockOpacity: 0,
    backgroundClassInfo: "transparent",
    backgroundClassInfoOpacity: 0,
    classInfoFontColor,
    clockFontStyle: defaultFontStyle,
    clockFontColor,
    agendaBackgroundColor: "transparent",
    agendaBackgroundOpacity: 0,
    agendaTitleFont: defaultFontStyle,
    agendaTitleColor: defaultFontColor,
    agendaStepFont: defaultFontStyle,
    agendaStepColor: defaultFontColor,
    agendaSubStepFont: defaultFontStyle,
    agendaSubStepColor: agendaSubStepColor || defaultFontColor,
    agendaActiveStepBackgroundColor,
    agendaActiveStepColor,
    agendaActiveSubStepColor: agendaActiveStepColor,
    agendaProgressTrackColor,
    agendaProgressFillColor: agendaProgressFillColor || agendaActiveStepBackgroundColor,
    agendaProgressScrubberColor: agendaActiveStepColor,
    surfaceShadow: "none",
    contentSurfaceShadow: "none",
    agendaSurfaceShadow: "none",
    marqueeSurfaceShadow: "none",
    clockSurfaceShadow: "none",
    reminderSurfaceShadow: "none",
    countdownSurfaceShadow: "none",
    surfaceOutlineWidth: 0,
    contentSurfaceOutlineWidth: 0,
    agendaSurfaceOutlineWidth: 0,
    clockSurfaceOutlineWidth: 0,
    reminderSurfaceOutlineWidth: reminderOutlineWidth,
    reminderSurfaceOutlineColor: reminderOutlineColor,
    reminderSurfaceOutlineStyle: "solid",
    countdownSurfaceOutlineWidth: countdownOutlineWidth,
    countdownSurfaceOutlineColor: countdownOutlineColor,
    countdownSurfaceOutlineStyle: "solid",
    reminderBackgroundColor,
    reminderFontColor,
    reminderFontStyle: defaultFontStyle,
    countdownBackgroundColor,
    countdownFontColor,
    countdownFontStyle: defaultFontStyle,
    countdownMessageFontStyle: defaultFontStyle,
    countdownMessageFontColor: countdownFontColor,
    surfaceCornerRadius,
    disableSurfaceInsetRim: true,
  };

  return {
    ...base,
    contentBoxStylePresets: buildTransparentContentBoxStylePresets(base),
  };
};

const getPresetMatchStyleDefaults = (styleDefaults = {}) => {
  if (!styleDefaults) return styleDefaults;
  const { contentBoxStylePresets, ...matchDefaults } = styleDefaults;
  return matchDefaults;
};

const RAW_CLASS_STYLE_PRESET_SCHEMES = [
  {
    id: "lesson-launchpad-default",
    label: "App Default",
    styleDefaults: {
      defaultFontStyle: "Helvetica",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#ffffff",
      background: "#f8fafc",
      backgroundAnnouncements: "#ffffff",
      backgroundClock: "#ffffff",
      surfaceShadow: "raised",
      clockFontStyle: "Helvetica",
      clockFontColor: "#0f172a",
      reminderBackgroundColor: "#ffffff",
      reminderFontColor: "#0f172a",
      reminderFontStyle: "Helvetica",
      countdownBackgroundColor: "#ffffff",
      countdownFontColor: "#0f172a",
      countdownFontStyle: "Helvetica",
    },
  },
  {
    id: "scholar-sky",
    label: "Scholar Sky",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#ffffff",
      background: "#eef6ff",
      backgroundAnnouncements: "#dbeafe",
      backgroundClock: "#dbeafe",
      surfaceShadow: "soft",
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#0f172a",
      reminderBackgroundColor: "#ffffff",
      reminderFontColor: "#0f172a",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#ffffff",
      countdownFontColor: "#0f172a",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "midnight-studio",
    label: "Midnight Studio",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#e2e8f0",
      cardBackgroundColor: "#0f172a",
      background: "#020617",
      backgroundAnnouncements: "#0f172a",
      backgroundClock: "#0f172a",
      surfaceShadow: "raised",
      surfaceCornerRadius: 0,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#22d3ee",
      reminderBackgroundColor: "#111827",
      reminderFontColor: "#f8fafc",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#111827",
      countdownFontColor: "#f8fafc",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "warm-bulletin",
    label: "Warm Bulletin",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#4a2f1f",
      cardBackgroundColor: "#fff8ef",
      background: "#fef3e2",
      backgroundAnnouncements: "#fde7c7",
      backgroundClock: "#f7d8a8",
      surfaceShadow: "soft",
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#7c2d12",
      reminderBackgroundColor: "#fff8ef",
      reminderFontColor: "#4a2f1f",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#fff1d6",
      countdownFontColor: "#7c2d12",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "pep-rally",
    label: "Pep Rally",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#1f2937",
      cardBackgroundColor: "#ffffff",
      background: "#fff7ed",
      backgroundAnnouncements: "#fdba74",
      backgroundClock: "#f97316",
      surfaceShadow: "glow",
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#ffffff",
      reminderBackgroundColor: "#ffedd5",
      reminderFontColor: "#9a3412",
      reminderFontStyle: "Permanent Marker, cursive",
      countdownBackgroundColor: "#fed7aa",
      countdownFontColor: "#9a3412",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "forest-study-hall",
    label: "Forest Study Hall",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#1b4332",
      cardBackgroundColor: "#f4fbf7",
      background: "#eaf7ef",
      backgroundAnnouncements: "#d9f2e1",
      backgroundClock: "#ccebd7",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#74c69d",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 16,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#1b4332",
      reminderBackgroundColor: "#f4fbf7",
      reminderFontColor: "#1b4332",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#ecf9f0",
      countdownFontColor: "#1b4332",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "sunrise-seminar",
    label: "Sunrise Seminar",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#7c2d12",
      cardBackgroundColor: "#fff7ef",
      background: "#fff1e6",
      backgroundAnnouncements: "#ffe4cc",
      backgroundClock: "#ffd8a8",
      surfaceShadow: "raised",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#fb923c",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#9a3412",
      reminderBackgroundColor: "#fff7ef",
      reminderFontColor: "#7c2d12",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#ffedd5",
      countdownFontColor: "#9a3412",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "aurora-lab",
    label: "Aurora Lab",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#f8fcff",
      background: "#e6f7ff",
      backgroundAnnouncements: "#d4f1ff",
      backgroundClock: "#bae6fd",
      surfaceShadow: "spotlight",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#38bdf8",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#0369a1",
      reminderBackgroundColor: "#f8fcff",
      reminderFontColor: "#0f172a",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#e0f2fe",
      countdownFontColor: "#075985",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "chalkboard-classic",
    label: "Chalkboard Classic",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#e2f7e7",
      cardBackgroundColor: "#1f3a2e",
      background: "#13271f",
      backgroundAnnouncements: "#1b3328",
      backgroundClock: "#244537",
      surfaceShadow: "deep",
      surfaceOutlineWidth: 1,
      surfaceOutlineColor: "#9fe3ba",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 12,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#dcfce7",
      reminderBackgroundColor: "#254437",
      reminderFontColor: "#dcfce7",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#254437",
      countdownFontColor: "#dcfce7",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "neon-night-shift",
    label: "Neon Night Shift",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#f8fafc",
      cardBackgroundColor: "#111827",
      background: "#020617",
      backgroundAnnouncements: "#111827",
      backgroundClock: "#0f172a",
      surfaceShadow: "glow",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#22d3ee",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 16,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#67e8f9",
      reminderBackgroundColor: "#1f2937",
      reminderFontColor: "#e0f2fe",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#1f2937",
      countdownFontColor: "#e0f2fe",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "matrix-terminal",
    label: "Matrix",
    styleDefaults: {
      defaultFontStyle: "Orbitron, sans-serif",
      defaultFontColor: "#39ff14",
      defaultFontSize: "20pt",
      cardBackgroundColor: "#000000",
      cardBackgroundOpacity: 1,
      background: "#000000",
      backgroundAnnouncements: "#000000",
      backgroundClock: "#000000",
      backgroundClassInfo: "#000000",
      surfaceShadow: "none",
      disableSurfaceInsetRim: true,
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#39ff14",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 4,
      marqueeSurfaceOutlineWidth: 0,
      clockSurfaceOutlineWidth: 2,
      clockSurfaceOutlineColor: "#39ff14",
      clockSurfaceOutlineStyle: "solid",
      contentSurfaceOutlineWidth: 2,
      contentSurfaceOutlineColor: "#39ff14",
      contentSurfaceOutlineStyle: "solid",
      agendaSurfaceOutlineWidth: 2,
      agendaSurfaceOutlineColor: "#39ff14",
      agendaSurfaceOutlineStyle: "dashed",
      agendaBackgroundColor: "#000000",
      agendaTitleColor: "#39ff14",
      agendaStepColor: "#39ff14",
      agendaSubStepColor: "#22c55e",
      agendaActiveStepBackgroundColor: "#39ff14",
      agendaActiveStepColor: "#000000",
      agendaActiveSubStepColor: "#000000",
      agendaProgressTrackColor: "rgba(57, 255, 20, 0.28)",
      agendaProgressFillColor: "#39ff14",
      agendaProgressScrubberColor: "#000000",
      reminderSurfaceOutlineWidth: 2,
      reminderSurfaceOutlineColor: "#39ff14",
      reminderSurfaceOutlineStyle: "dashed",
      reminderBackgroundColor: "#000000",
      reminderFontColor: "#39ff14",
      reminderFontStyle: "Orbitron, sans-serif",
      countdownSurfaceOutlineWidth: 2,
      countdownSurfaceOutlineColor: "#39ff14",
      countdownSurfaceOutlineStyle: "solid",
      countdownBackgroundColor: "#000000",
      countdownFontColor: "#39ff14",
      countdownFontStyle: "Orbitron, sans-serif",
      countdownMessageFontColor: "#39ff14",
      countdownMessageFontStyle: "Orbitron, sans-serif",
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#39ff14",
      classInfoFontColor: "#39ff14",
      contentBoxStylePresets: [
        {
          id: "content-box-preset-1",
          label: "Box Preset 1",
          defaultFontStyle: "Orbitron, sans-serif",
          defaultFontSize: "20pt",
          defaultFontColor: "#39ff14",
          cardBackgroundColor: "#000000",
          cardBackgroundOpacity: 1,
          contentSurfaceShadow: "none",
          contentSurfaceOutlineWidth: 2,
          contentSurfaceOutlineColor: "#39ff14",
          contentSurfaceOutlineStyle: "solid",
          contentSurfaceCornerRadius: 4,
        },
        {
          id: "content-box-preset-2",
          label: "Box Preset 2",
          defaultFontStyle: "Orbitron, sans-serif",
          defaultFontSize: "24pt",
          defaultFontColor: "#000000",
          cardBackgroundColor: "#39ff14",
          cardBackgroundOpacity: 1,
          contentSurfaceShadow: "none",
          contentSurfaceOutlineWidth: 2,
          contentSurfaceOutlineColor: "#39ff14",
          contentSurfaceOutlineStyle: "dashed",
          contentSurfaceCornerRadius: 4,
        },
        {
          id: "content-box-preset-3",
          label: "Box Preset 3",
          defaultFontStyle: "Orbitron, sans-serif",
          defaultFontSize: "18pt",
          defaultFontColor: "#39ff14",
          cardBackgroundColor: "#0a0f0a",
          cardBackgroundOpacity: 1,
          contentSurfaceShadow: "none",
          contentSurfaceOutlineWidth: 2,
          contentSurfaceOutlineColor: "#22c55e",
          contentSurfaceOutlineStyle: "dashed",
          contentSurfaceCornerRadius: 4,
        },
        {
          id: "content-box-preset-4",
          label: "Box Preset 4",
          defaultFontStyle: "Orbitron, sans-serif",
          defaultFontSize: "20pt",
          defaultFontColor: "#39ff14",
          cardBackgroundColor: "#000000",
          cardBackgroundOpacity: 1,
          contentSurfaceShadow: "none",
          contentSurfaceOutlineWidth: 2,
          contentSurfaceOutlineColor: "#39ff14",
          contentSurfaceOutlineStyle: "solid",
          contentSurfaceCornerRadius: 4,
        },
      ],
    },
  },
  {
    id: "lavender-focus",
    label: "Lavender Focus",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#4c1d95",
      cardBackgroundColor: "#faf5ff",
      background: "#f5edff",
      backgroundAnnouncements: "#ede2ff",
      backgroundClock: "#ddd6fe",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#a78bfa",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 20,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#5b21b6",
      reminderBackgroundColor: "#faf5ff",
      reminderFontColor: "#4c1d95",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#f3e8ff",
      countdownFontColor: "#5b21b6",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "retro-poster",
    label: "Retro Poster",
    styleDefaults: {
      defaultFontStyle: "Permanent Marker, cursive",
      defaultFontColor: "#3b1f2b",
      cardBackgroundColor: "#fff7d6",
      background: "#fff1b8",
      backgroundAnnouncements: "#ffe08a",
      backgroundClock: "#fbbf24",
      surfaceShadow: "raised",
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#f97316",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 10,
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#7c2d12",
      reminderBackgroundColor: "#fff8dc",
      reminderFontColor: "#3b1f2b",
      reminderFontStyle: "Permanent Marker, cursive",
      countdownBackgroundColor: "#ffedd5",
      countdownFontColor: "#9a3412",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "minimal-slate",
    label: "Minimal Slate",
    styleDefaults: {
      defaultFontStyle: "Arial, Helvetica, sans-serif",
      defaultFontColor: "#111827",
      cardBackgroundColor: "#f8fafc",
      background: "#f1f5f9",
      backgroundAnnouncements: "#e2e8f0",
      backgroundClock: "#cbd5e1",
      surfaceShadow: "none",
      surfaceOutlineWidth: 1,
      surfaceOutlineColor: "#94a3b8",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Arial, Helvetica, sans-serif",
      clockFontColor: "#0f172a",
      reminderBackgroundColor: "#f8fafc",
      reminderFontColor: "#111827",
      reminderFontStyle: "Arial, Helvetica, sans-serif",
      countdownBackgroundColor: "#e2e8f0",
      countdownFontColor: "#1f2937",
      countdownFontStyle: "Arial, Helvetica, sans-serif",
    },
  },
  {
    id: "ocean-depth",
    label: "Ocean Depth",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#e0f2fe",
      cardBackgroundColor: "#0b253a",
      background: "#071a2c",
      backgroundAnnouncements: "#0c2f4a",
      backgroundClock: "#0f3f61",
      surfaceShadow: "dramatic",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#38bdf8",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 16,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#bae6fd",
      reminderBackgroundColor: "#123b5a",
      reminderFontColor: "#e0f2fe",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#123b5a",
      countdownFontColor: "#e0f2fe",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "sunset-coral",
    label: "Sunset Coral",
    styleDefaults: {
      defaultFontStyle: "Verdana, Geneva, sans-serif",
      defaultFontColor: "#7f1d1d",
      cardBackgroundColor: "#fff5f5",
      background: "#ffe4e6",
      backgroundAnnouncements: "#fecdd3",
      backgroundClock: "#fb7185",
      surfaceShadow: "glow",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#fb7185",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Verdana, Geneva, sans-serif",
      clockFontColor: "#fff1f2",
      reminderBackgroundColor: "#fff5f5",
      reminderFontColor: "#7f1d1d",
      reminderFontStyle: "Verdana, Geneva, sans-serif",
      countdownBackgroundColor: "#ffe4e6",
      countdownFontColor: "#9f1239",
      countdownFontStyle: "Verdana, Geneva, sans-serif",
    },
  },
  {
    id: "royal-lecture",
    label: "Royal Lecture",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#f8fafc",
      cardBackgroundColor: "#1e293b",
      background: "#0f172a",
      backgroundAnnouncements: "#1e293b",
      backgroundClock: "#334155",
      surfaceShadow: "deep",
      surfaceOutlineWidth: 5,
      surfaceOutlineColor: "#fbbf24",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 0,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#fde68a",
      reminderBackgroundColor: "#334155",
      reminderFontColor: "#fde68a",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#334155",
      countdownFontColor: "#fde68a",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "graph-paper-lab",
    label: "Graph Paper",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#1e3a8a",
      cardBackgroundColor: "#f8fbff",
      background: "#edf4ff",
      backgroundAnnouncements: "#dbeafe",
      backgroundClock: "#bfdbfe",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#60a5fa",
      surfaceOutlineStyle: "dotted",
      surfaceCornerRadius: 0,
      clockFontStyle: "Roboto, sans-serif",
      clockFontColor: "#1d4ed8",
      reminderBackgroundColor: "#f8fbff",
      reminderFontColor: "#1e3a8a",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#e0edff",
      countdownFontColor: "#1d4ed8",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "mint-notebook",
    label: "Mint Notebook",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#0f3d2e",
      cardBackgroundColor: "#f2fbf7",
      background: "#e6f7ef",
      backgroundAnnouncements: "#d3f0e2",
      backgroundClock: "#bde8d3",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#34d399",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 20,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#065f46",
      reminderBackgroundColor: "#f2fbf7",
      reminderFontColor: "#0f3d2e",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#dcfce7",
      countdownFontColor: "#065f46",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "steel-blue-studio",
    label: "Steel Blue Studio",
    styleDefaults: {
      defaultFontStyle: "Arial, Helvetica, sans-serif",
      defaultFontColor: "#1e293b",
      cardBackgroundColor: "#f8fbff",
      background: "#eef2f7",
      backgroundAnnouncements: "#dbe3ee",
      backgroundClock: "#c7d3e3",
      surfaceShadow: "raised",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#64748b",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Arial, Helvetica, sans-serif",
      clockFontColor: "#0f172a",
      reminderBackgroundColor: "#f8fbff",
      reminderFontColor: "#1e293b",
      reminderFontStyle: "Arial, Helvetica, sans-serif",
      countdownBackgroundColor: "#e2e8f0",
      countdownFontColor: "#1e293b",
      countdownFontStyle: "Arial, Helvetica, sans-serif",
    },
  },
  {
    id: "citrus-pop",
    label: "Citrus Pop",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#365314",
      cardBackgroundColor: "#fefce8",
      background: "#fef9c3",
      backgroundAnnouncements: "#fde047",
      backgroundClock: "#a3e635",
      surfaceShadow: "glow",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#84cc16",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 16,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#14532d",
      reminderBackgroundColor: "#fff9db",
      reminderFontColor: "#365314",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#ecfccb",
      countdownFontColor: "#3f6212",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "plum-academy",
    label: "Plum Academy",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#f5f3ff",
      cardBackgroundColor: "#3b1f4a",
      background: "#2e143b",
      backgroundAnnouncements: "#4c1d60",
      backgroundClock: "#6b2d8b",
      surfaceShadow: "deep",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#c084fc",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#e9d5ff",
      reminderBackgroundColor: "#55306b",
      reminderFontColor: "#f3e8ff",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#55306b",
      countdownFontColor: "#f3e8ff",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "sunny-homeroom",
    label: "Sunny Homeroom",
    styleDefaults: {
      defaultFontStyle: "Verdana, Geneva, sans-serif",
      defaultFontColor: "#713f12",
      cardBackgroundColor: "#fffdf0",
      background: "#fef9c3",
      backgroundAnnouncements: "#fde68a",
      backgroundClock: "#facc15",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#f59e0b",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 22,
      clockFontStyle: "Verdana, Geneva, sans-serif",
      clockFontColor: "#7c2d12",
      reminderBackgroundColor: "#fffdf0",
      reminderFontColor: "#713f12",
      reminderFontStyle: "Verdana, Geneva, sans-serif",
      countdownBackgroundColor: "#fef3c7",
      countdownFontColor: "#78350f",
      countdownFontStyle: "Verdana, Geneva, sans-serif",
    },
  },
  {
    id: "night-ops",
    label: "Night Ops",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#e2e8f0",
      cardBackgroundColor: "#0b1220",
      background: "#020617",
      backgroundAnnouncements: "#0b1220",
      backgroundClock: "#111827",
      surfaceShadow: "spotlight",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#22d3ee",
      surfaceOutlineStyle: "dotted",
      surfaceCornerRadius: 0,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#67e8f9",
      reminderBackgroundColor: "#1e293b",
      reminderFontColor: "#bae6fd",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#1e293b",
      countdownFontColor: "#bae6fd",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "rose-gold",
    label: "Rose Gold",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#7a2846",
      cardBackgroundColor: "#fff8fb",
      background: "#ffeaf2",
      backgroundAnnouncements: "#ffd6e8",
      backgroundClock: "#f9a8d4",
      surfaceShadow: "dramatic",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#f472b6",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#9d174d",
      reminderBackgroundColor: "#fff8fb",
      reminderFontColor: "#7a2846",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#ffe4ef",
      countdownFontColor: "#9d174d",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "indigo-workshop",
    label: "Indigo Workshop",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#e0e7ff",
      cardBackgroundColor: "#1f2a5a",
      background: "#172554",
      backgroundAnnouncements: "#1e3a8a",
      backgroundClock: "#312e81",
      surfaceShadow: "raised",
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#818cf8",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 12,
      clockFontStyle: "Roboto, sans-serif",
      clockFontColor: "#c7d2fe",
      reminderBackgroundColor: "#2d3b79",
      reminderFontColor: "#c7d2fe",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#2d3b79",
      countdownFontColor: "#c7d2fe",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "strawberry-fields",
    label: "Strawberry Fields",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#0f3d2e",
      cardBackgroundColor: "#ff6060",
      background: "#1eaa67",
      backgroundAnnouncements: "#ff6060",
      backgroundClock: "#ff6060",
      surfaceShadow: "soft",
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#ffdb49",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 20,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#065f46",
      reminderBackgroundColor: "#f2fbf7",
      reminderFontColor: "#0f3d2e",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#dcfce7",
      countdownFontColor: "#065f46",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "midnight-glow",
    label: "Midnight Glow",
    styleDefaults: {
      defaultFontStyle: "Permanent Marker, cursive",
      defaultFontColor: "#e0e7ff",
      cardBackgroundColor: "#1f2a5a",
      background: "#00114b",
      backgroundAnnouncements: "#1f2a5a",
      backgroundClock: "#1f2a5a",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 0,
        offsetY: 0,
        blur: 4,
        spread: 2,
        color: "#ffffff",
        opacity: 1,
      },      
      surfaceOutlineWidth: 0,
      surfaceOutlineColor: "#818cf8",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Permanent Marker, cursive",
      clockFontColor: "#c7d2fe",
      reminderBackgroundColor: "#2d3b79",
      reminderFontColor: "#c7d2fe",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#2d3b79",
      countdownFontColor: "#c7d2fe",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "retro-metro",
    label: "Retro Metro",
    styleDefaults: {
      defaultFontStyle: "Righteous, cursive",
      defaultFontColor: "#666547",
      cardBackgroundColor: "#FFFEB3",
      background: "#FFE28A",
      backgroundAnnouncements: "#6FCB9F",
      backgroundClock: "#FB2E01",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 1,
        offsetY: 1,
        blur: 0,
        spread: 2,
        color: "#666547",
        opacity: 0.48,
      },      
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#666547",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Righteous, cursive",
      clockFontColor: "#FFFEB3",
      reminderBackgroundColor: "#FFFEB3",
      reminderFontColor: "#666547",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#FB2E01",
      countdownFontColor: "#FFFEB3",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "comic-classroom",
    label: "Comic Classroom",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#111827",
      cardBackgroundColor: "#fff7ed",
      background: "#fde047",
      backgroundAnnouncements: "#ef4444",
      backgroundClock: "#2563eb",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 0,
        color: "#111827",
        opacity: 0.7,
      },
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#111827",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 8,
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#ffffff",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#111827",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#22c55e",
      countdownFontColor: "#052e16",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "arcade-after-school",
    label: "Arcade After School",
    styleDefaults: {
      defaultFontStyle: "Orbitron, sans-serif",
      defaultFontColor: "#f5d0fe",
      cardBackgroundColor: "#16001f",
      background: "#090018",
      backgroundAnnouncements: "#1f1147",
      backgroundClock: "#ff2bd6",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#00e5ff",
        opacity: 0.65,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#ff2bd6",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 10,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#16001f",
      reminderBackgroundColor: "#00e5ff",
      reminderFontColor: "#09111f",
      reminderFontStyle: "Orbitron, sans-serif",
      countdownBackgroundColor: "#00e5ff",
      countdownFontColor: "#09111f",
      countdownFontStyle: "Orbitron, sans-serif",
    },
  },
  {
    id: "terrazzo-tuesday",
    label: "Terrazzo Tuesday",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#214761",
      cardBackgroundColor: "#fffaf0",
      background: "#fdebd3",
      backgroundAnnouncements: "#ff6b6b",
      backgroundClock: "#7dd3fc",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 3,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#1f2937",
        opacity: 0.42,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#1f2937",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#214761",
      reminderBackgroundColor: "#fffaf0",
      reminderFontColor: "#214761",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#f9a8d4",
      countdownFontColor: "#831843",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "desert-workshop",
    label: "Desert Workshop",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#78350f",
      cardBackgroundColor: "#fff7ed",
      background: "#fed7aa",
      backgroundAnnouncements: "#fdba74",
      backgroundClock: "#c2410c",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#7c2d12",
        opacity: 0.5,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#92400e",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#ffedd5",
      reminderFontColor: "#78350f",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#f97316",
      countdownFontColor: "#fff7ed",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "poolside-planner",
    label: "Poolside Planner",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#134e4a",
      cardBackgroundColor: "#ecfeff",
      background: "#a7f3d0",
      backgroundAnnouncements: "#5eead4",
      backgroundClock: "#0891b2",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#0f766e",
        opacity: 0.52,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#0f766e",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 20,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#ecfeff",
      reminderBackgroundColor: "#ecfeff",
      reminderFontColor: "#134e4a",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#ccfbf1",
      countdownFontColor: "#115e59",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "berry-binder",
    label: "Berry Binder",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#831843",
      cardBackgroundColor: "#fff1f2",
      background: "#fbcfe8",
      backgroundAnnouncements: "#f472b6",
      backgroundClock: "#be185d",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#831843",
        opacity: 0.45,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#9d174d",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 16,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#fff1f2",
      reminderBackgroundColor: "#fff1f2",
      reminderFontColor: "#831843",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#f9a8d4",
      countdownFontColor: "#831843",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "traffic-light-lab",
    label: "Traffic Light Lab",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#1f2937",
      cardBackgroundColor: "#fefce8",
      background: "#dcfce7",
      backgroundAnnouncements: "#facc15",
      backgroundClock: "#dc2626",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#14532d",
        opacity: 0.48,
      },
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#16a34a",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#fefce8",
      reminderFontColor: "#365314",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#16a34a",
      countdownFontColor: "#f0fdf4",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "blueprint-bold",
    label: "Blueprint Bold",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#dbeafe",
      cardBackgroundColor: "#1e3a8a",
      background: "#172554",
      backgroundAnnouncements: "#1d4ed8",
      backgroundClock: "#f59e0b",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#020617",
        opacity: 0.62,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#93c5fd",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 6,
      clockFontStyle: "Roboto, sans-serif",
      clockFontColor: "#172554",
      reminderBackgroundColor: "#f59e0b",
      reminderFontColor: "#172554",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#f59e0b",
      countdownFontColor: "#172554",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "paper-cutouts",
    label: "Paper Cutouts",
    styleDefaults: {
      defaultFontStyle: "Permanent Marker, cursive",
      defaultFontColor: "#334155",
      cardBackgroundColor: "#fff7ed",
      background: "#fef3c7",
      backgroundAnnouncements: "#bfdbfe",
      backgroundClock: "#f97316",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 3,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#334155",
        opacity: 0.42,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#334155",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 4,
      clockFontStyle: "Permanent Marker, cursive",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#334155",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#2563eb",
      countdownFontColor: "#eff6ff",
      countdownFontStyle: "Permanent Marker, cursive",
    },
  },
  {
    id: "jungle-gym",
    label: "Jungle Gym",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#14532d",
      cardBackgroundColor: "#f0fdf4",
      background: "#86efac",
      backgroundAnnouncements: "#bbf7d0",
      backgroundClock: "#f97316",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#166534",
        opacity: 0.48,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#15803d",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#f97316",
      reminderFontColor: "#fff7ed",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#f97316",
      countdownFontColor: "#fff7ed",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "space-cadet",
    label: "Space Cadet",
    styleDefaults: {
      defaultFontStyle: "Orbitron, sans-serif",
      defaultFontColor: "#e0e7ff",
      cardBackgroundColor: "#1e1b4b",
      background: "#0f102f",
      backgroundAnnouncements: "#312e81",
      backgroundClock: "#facc15",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#020617",
        opacity: 0.72,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#a78bfa",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#111827",
      reminderBackgroundColor: "#facc15",
      reminderFontColor: "#111827",
      reminderFontStyle: "Orbitron, sans-serif",
      countdownBackgroundColor: "#facc15",
      countdownFontColor: "#111827",
      countdownFontStyle: "Orbitron, sans-serif",
    },
  },
  {
    id: "bubblegum-board",
    label: "Bubblegum Board",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#831843",
      cardBackgroundColor: "#fff1f2",
      background: "#fbcfe8",
      backgroundAnnouncements: "#f9a8d4",
      backgroundClock: "#ec4899",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#8b5cf6",
        opacity: 0.52,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#8b5cf6",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 24,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#fff1f2",
      reminderBackgroundColor: "#8b5cf6",
      reminderFontColor: "#f5f3ff",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#8b5cf6",
      countdownFontColor: "#f5f3ff",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "marker-box",
    label: "Marker Box",
    styleDefaults: {
      defaultFontStyle: "Arial, Helvetica, sans-serif",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#ffffff",
      background: "#f8fafc",
      backgroundAnnouncements: "#fee2e2",
      backgroundClock: "#2563eb",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 0,
        color: "#64748b",
        opacity: 0.5,
      },
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#0f172a",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 6,
      clockFontStyle: "Arial, Helvetica, sans-serif",
      clockFontColor: "#ffffff",
      reminderBackgroundColor: "#ffffff",
      reminderFontColor: "#0f172a",
      reminderFontStyle: "Arial, Helvetica, sans-serif",
      countdownBackgroundColor: "#ef4444",
      countdownFontColor: "#ffffff",
      countdownFontStyle: "Arial, Helvetica, sans-serif",
    },
  },
  {
    id: "limeade-lecture",
    label: "Limeade Lecture",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#365314",
      cardBackgroundColor: "#f7fee7",
      background: "#d9f99d",
      backgroundAnnouncements: "#bef264",
      backgroundClock: "#65a30d",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#0f766e",
        opacity: 0.48,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#0f766e",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 16,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#f7fee7",
      reminderBackgroundColor: "#f7fee7",
      reminderFontColor: "#365314",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#0f766e",
      countdownFontColor: "#ecfdf5",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "clay-table",
    label: "Clay Table",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#713f12",
      cardBackgroundColor: "#fffbeb",
      background: "#fef3c7",
      backgroundAnnouncements: "#fde68a",
      backgroundClock: "#a16207",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 5,
        blur: 0,
        spread: 1,
        color: "#7c2d12",
        opacity: 0.42,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#7c2d12",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 10,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#fffbeb",
      reminderFontColor: "#713f12",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#d97706",
      countdownFontColor: "#fffbeb",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "rainy-day-reading",
    label: "Rainy Day Reading",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#0c4a6e",
      cardBackgroundColor: "#f0f9ff",
      background: "#dbeafe",
      backgroundAnnouncements: "#bae6fd",
      backgroundClock: "#0369a1",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#64748b",
        opacity: 0.46,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#64748b",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 18,
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#f0f9ff",
      reminderBackgroundColor: "#f0f9ff",
      reminderFontColor: "#0c4a6e",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#0ea5e9",
      countdownFontColor: "#f0f9ff",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "electric-peach",
    label: "Electric Peach",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#7c2d12",
      cardBackgroundColor: "#fff7ed",
      background: "#fed7aa",
      backgroundAnnouncements: "#fb7185",
      backgroundClock: "#f97316",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#db2777",
        opacity: 0.45,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#db2777",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#7c2d12",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#db2777",
      countdownFontColor: "#fff1f2",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "primary-palette",
    label: "Primary Palette",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#172554",
      cardBackgroundColor: "#eff6ff",
      background: "#fde047",
      backgroundAnnouncements: "#dc2626",
      backgroundClock: "#1d4ed8",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 1,
        color: "#172554",
        opacity: 0.58,
      },
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#172554",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 2,
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#eff6ff",
      reminderBackgroundColor: "#eff6ff",
      reminderFontColor: "#172554",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#dc2626",
      countdownFontColor: "#fff7ed",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "book-fair",
    label: "Book Fair",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#4a2f1f",
      cardBackgroundColor: "#fffaf0",
      background: "#fef3c7",
      backgroundAnnouncements: "#fde68a",
      backgroundClock: "#7c3aed",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 5,
        blur: 0,
        spread: 0,
        color: "#d97706",
        opacity: 0.46,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#d97706",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 12,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#f5f3ff",
      reminderBackgroundColor: "#fffaf0",
      reminderFontColor: "#4a2f1f",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#7c3aed",
      countdownFontColor: "#f5f3ff",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "chalk-pop",
    label: "Chalk Pop",
    styleDefaults: {
      defaultFontStyle: "Permanent Marker, cursive",
      defaultFontColor: "#dcfce7",
      cardBackgroundColor: "#14532d",
      background: "#052e16",
      backgroundAnnouncements: "#166534",
      backgroundClock: "#166534",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 4,
        offsetY: 4,
        blur: 0,
        spread: 1,
        color: "#fde68a",
        opacity: 0.52,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#fde68a",
      surfaceOutlineStyle: "dashed",
      surfaceCornerRadius: 10,
      clockFontStyle: "Permanent Marker, cursive",
      clockFontColor: "#dcfce7",
      reminderBackgroundColor: "#14532d",
      reminderFontColor: "#dcfce7",
      reminderFontStyle: "Permanent Marker, cursive",
      countdownBackgroundColor: "#14532d",
      countdownFontColor: "#dcfce7",
      countdownFontStyle: "Permanent Marker, cursive",
    },
  },
  {
    id: "racecar-red",
    label: "Racecar Red",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#111827",
      cardBackgroundColor: "#fff7ed",
      background: "#ff3131",
      backgroundAnnouncements: "#ff1f1f",
      backgroundClock: "#111827",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 1,
        color: "#111827",
        opacity: 0.7,
      },
      surfaceOutlineWidth: 3,
      surfaceOutlineColor: "#111827",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 6,
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#ffffff",
      reminderBackgroundColor: "#fff1f1",
      reminderFontColor: "#111827",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#ff1f1f",
      countdownFontColor: "#ffffff",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "grand-canyon",
    label: "Grand Canyon",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#7c2d12",
      cardBackgroundColor: "#fff7ed",
      background: "#f8d49b",
      backgroundAnnouncements: "#fdba74",
      backgroundClock: "#c2410c",
      surfaceShadow: "custom",
      surfaceShadowCustom: {
        offsetX: 5,
        offsetY: 5,
        blur: 0,
        spread: 1,
        color: "#7c2d12",
        opacity: 0.46,
      },
      surfaceOutlineWidth: 2,
      surfaceOutlineColor: "#92400e",
      surfaceOutlineStyle: "solid",
      surfaceCornerRadius: 14,
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#fff7ed",
      reminderBackgroundColor: "#ffedd5",
      reminderFontColor: "#7c2d12",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#ea580c",
      countdownFontColor: "#fff7ed",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "sunrise-spectrum-gradient",
    label: "Sunrise Spectrum",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#7c2d12",
      cardBackgroundColor: "#fff7ed",
      background: "linear-gradient(135deg, #fef3c7 0%, #fdba74 48%, #fb7185 100%)",
      backgroundAnnouncements: "#ffedd5",
      backgroundClock: "#fed7aa",
      surfaceShadow: "raised",
      agendaActiveStepBackgroundColor: "#f97316",
      agendaActiveStepColor: "#fff7ed",
      agendaActiveSubStepColor: "#fff7ed",
      agendaProgressFillColor: "#fb923c",
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#9a3412",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#7c2d12",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#ffedd5",
      countdownFontColor: "#9a3412",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "aurora-hall-gradient",
    label: "Aurora Hall",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#f8fcff",
      background: "linear-gradient(140deg, #cffafe 0%, #a7f3d0 50%, #bfdbfe 100%)",
      backgroundAnnouncements: "#ecfeff",
      backgroundClock: "#dbeafe",
      surfaceShadow: "soft",
      agendaActiveStepBackgroundColor: "#0ea5a4",
      agendaActiveStepColor: "#ecfeff",
      agendaActiveSubStepColor: "#ecfeff",
      agendaProgressFillColor: "#14b8a6",
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#075985",
      reminderBackgroundColor: "#f0fdfa",
      reminderFontColor: "#0f172a",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#e0f2fe",
      countdownFontColor: "#075985",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "cosmic-lesson-gradient",
    label: "Cosmic Lesson",
    styleDefaults: {
      defaultFontStyle: "Orbitron, sans-serif",
      defaultFontColor: "#e9d5ff",
      cardBackgroundColor: "#1e1b4b",
      background: "linear-gradient(145deg, #020617 0%, #312e81 52%, #a21caf 100%)",
      backgroundAnnouncements: "#312e81",
      backgroundClock: "#4c1d95",
      surfaceShadow: "glow",
      agendaActiveStepBackgroundColor: "#a21caf",
      agendaActiveStepColor: "#fdf4ff",
      agendaActiveSubStepColor: "#fdf4ff",
      agendaProgressFillColor: "#c026d3",
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#22d3ee",
      reminderBackgroundColor: "#2e1065",
      reminderFontColor: "#f5d0fe",
      reminderFontStyle: "Orbitron, sans-serif",
      countdownBackgroundColor: "#1e1b4b",
      countdownFontColor: "#67e8f9",
      countdownFontStyle: "Orbitron, sans-serif",
    },
  },
  {
    id: "chalk-mint-gradient",
    label: "Chalk Mint",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#ecfeff",
      cardBackgroundColor: "#164e63",
      background: "linear-gradient(130deg, #0f172a 0%, #164e63 52%, #14b8a6 100%)",
      backgroundAnnouncements: "#0f766e",
      backgroundClock: "#134e4a",
      surfaceShadow: "deep",
      agendaActiveStepBackgroundColor: "#0f766e",
      agendaActiveStepColor: "#ecfeff",
      agendaActiveSubStepColor: "#ecfeff",
      agendaProgressFillColor: "#14b8a6",
      clockFontStyle: "Roboto, sans-serif",
      clockFontColor: "#99f6e4",
      reminderBackgroundColor: "#115e59",
      reminderFontColor: "#ccfbf1",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#134e4a",
      countdownFontColor: "#99f6e4",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "berry-pop-gradient",
    label: "Berry Pop",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#4a044e",
      cardBackgroundColor: "#fff1f2",
      background: "linear-gradient(135deg, #fce7f3 0%, #f9a8d4 48%, #f43f5e 100%)",
      backgroundAnnouncements: "#fecdd3",
      backgroundClock: "#fda4af",
      surfaceShadow: "spotlight",
      agendaActiveStepBackgroundColor: "#e11d48",
      agendaActiveStepColor: "#fff1f2",
      agendaActiveSubStepColor: "#fff1f2",
      agendaProgressFillColor: "#f43f5e",
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#881337",
      reminderBackgroundColor: "#ffe4e6",
      reminderFontColor: "#831843",
      reminderFontStyle: "Permanent Marker, cursive",
      countdownBackgroundColor: "#fecdd3",
      countdownFontColor: "#9f1239",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "skyline-notes-gradient",
    label: "Skyline Notes",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#0f172a",
      cardBackgroundColor: "#eff6ff",
      background: "linear-gradient(160deg, #dbeafe 0%, #a5b4fc 50%, #818cf8 100%)",
      backgroundAnnouncements: "#c7d2fe",
      backgroundClock: "#bfdbfe",
      surfaceShadow: "raised",
      agendaActiveStepBackgroundColor: "#4f46e5",
      agendaActiveStepColor: "#eef2ff",
      agendaActiveSubStepColor: "#eef2ff",
      agendaProgressFillColor: "#6366f1",
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#1e3a8a",
      reminderBackgroundColor: "#e0e7ff",
      reminderFontColor: "#1e3a8a",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#dbeafe",
      countdownFontColor: "#1d4ed8",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "lime-splash-gradient",
    label: "Lime Splash",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#365314",
      cardBackgroundColor: "#f7fee7",
      background: "linear-gradient(135deg, #ecfccb 0%, #bef264 46%, #84cc16 100%)",
      backgroundAnnouncements: "#d9f99d",
      backgroundClock: "#bef264",
      surfaceShadow: "soft",
      agendaActiveStepBackgroundColor: "#65a30d",
      agendaActiveStepColor: "#f7fee7",
      agendaActiveSubStepColor: "#f7fee7",
      agendaProgressFillColor: "#84cc16",
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#3f6212",
      reminderBackgroundColor: "#ecfccb",
      reminderFontColor: "#365314",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#d9f99d",
      countdownFontColor: "#3f6212",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "twilight-ink-gradient",
    label: "Twilight Ink",
    styleDefaults: {
      defaultFontStyle: "Georgia, serif",
      defaultFontColor: "#e2e8f0",
      cardBackgroundColor: "#1f2937",
      background: "linear-gradient(140deg, #111827 0%, #334155 48%, #0ea5e9 100%)",
      backgroundAnnouncements: "#1e293b",
      backgroundClock: "#334155",
      surfaceShadow: "dramatic",
      agendaActiveStepBackgroundColor: "#0ea5e9",
      agendaActiveStepColor: "#082f49",
      agendaActiveSubStepColor: "#082f49",
      agendaProgressFillColor: "#38bdf8",
      clockFontStyle: "Georgia, serif",
      clockFontColor: "#bae6fd",
      reminderBackgroundColor: "#1f2937",
      reminderFontColor: "#e2e8f0",
      reminderFontStyle: "Georgia, serif",
      countdownBackgroundColor: "#334155",
      countdownFontColor: "#e2e8f0",
      countdownFontStyle: "Georgia, serif",
    },
  },
  {
    id: "lava-lab-gradient",
    label: "Lava Lab",
    styleDefaults: {
      defaultFontStyle: "Bangers, cursive",
      defaultFontColor: "#3f0a0a",
      cardBackgroundColor: "#fff7ed",
      background: "linear-gradient(132deg, #fee2e2 0%, #fb7185 45%, #ea580c 100%)",
      backgroundAnnouncements: "#fecaca",
      backgroundClock: "#fdba74",
      surfaceShadow: "hard",
      agendaActiveStepBackgroundColor: "#dc2626",
      agendaActiveStepColor: "#fff7ed",
      agendaActiveSubStepColor: "#fff7ed",
      agendaProgressFillColor: "#f97316",
      clockFontStyle: "Bangers, cursive",
      clockFontColor: "#7f1d1d",
      reminderBackgroundColor: "#ffe4e6",
      reminderFontColor: "#7f1d1d",
      reminderFontStyle: "Permanent Marker, cursive",
      countdownBackgroundColor: "#fed7aa",
      countdownFontColor: "#7c2d12",
      countdownFontStyle: "Bangers, cursive",
    },
  },
  {
    id: "ocean-floor-gradient",
    label: "Ocean Floor",
    styleDefaults: {
      defaultFontStyle: "Roboto, sans-serif",
      defaultFontColor: "#dbeafe",
      cardBackgroundColor: "#0f172a",
      background: "linear-gradient(150deg, #082f49 0%, #0f172a 50%, #0e7490 100%)",
      backgroundAnnouncements: "#0f172a",
      backgroundClock: "#164e63",
      surfaceShadow: "deep",
      agendaActiveStepBackgroundColor: "#0e7490",
      agendaActiveStepColor: "#ecfeff",
      agendaActiveSubStepColor: "#ecfeff",
      agendaProgressFillColor: "#06b6d4",
      clockFontStyle: "Roboto, sans-serif",
      clockFontColor: "#67e8f9",
      reminderBackgroundColor: "#0f172a",
      reminderFontColor: "#bae6fd",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#164e63",
      countdownFontColor: "#cffafe",
      countdownFontStyle: "Roboto, sans-serif",
    },
  },
  {
    id: "candy-cloud-gradient",
    label: "Candy Cloud",
    styleDefaults: {
      defaultFontStyle: "Poppins, sans-serif",
      defaultFontColor: "#6b21a8",
      cardBackgroundColor: "#fdf4ff",
      background: "linear-gradient(145deg, #fae8ff 0%, #ddd6fe 50%, #f9a8d4 100%)",
      backgroundAnnouncements: "#f5d0fe",
      backgroundClock: "#e9d5ff",
      surfaceShadow: "soft",
      surfaceCornerRadius: 24,
      agendaActiveStepBackgroundColor: "#c026d3",
      agendaActiveStepColor: "#fdf4ff",
      agendaActiveSubStepColor: "#fdf4ff",
      agendaProgressFillColor: "#e879f9",
      clockFontStyle: "Poppins, sans-serif",
      clockFontColor: "#7e22ce",
      reminderBackgroundColor: "#fdf4ff",
      reminderFontColor: "#7e22ce",
      reminderFontStyle: "Poppins, sans-serif",
      countdownBackgroundColor: "#f5d0fe",
      countdownFontColor: "#86198f",
      countdownFontStyle: "Poppins, sans-serif",
    },
  },
  {
    id: "stormfront-gradient",
    label: "Stormfront",
    styleDefaults: {
      defaultFontStyle: "Orbitron, sans-serif",
      defaultFontColor: "#e2e8f0",
      cardBackgroundColor: "#1e293b",
      background: "linear-gradient(145deg, #0f172a 0%, #475569 50%, #06b6d4 100%)",
      backgroundAnnouncements: "#334155",
      backgroundClock: "#1e293b",
      surfaceShadow: "dramatic",
      agendaActiveStepBackgroundColor: "#0891b2",
      agendaActiveStepColor: "#ecfeff",
      agendaActiveSubStepColor: "#ecfeff",
      agendaProgressFillColor: "#22d3ee",
      clockFontStyle: "Orbitron, sans-serif",
      clockFontColor: "#67e8f9",
      reminderBackgroundColor: "#334155",
      reminderFontColor: "#e2e8f0",
      reminderFontStyle: "Roboto, sans-serif",
      countdownBackgroundColor: "#1e293b",
      countdownFontColor: "#67e8f9",
      countdownFontStyle: "Orbitron, sans-serif",
    },
  },
  {
    id: "sunbeam-radial-gradient",
    label: "Sunbeam Radial",
    styleDefaults: {
      defaultFontStyle: "Nunito, sans-serif",
      defaultFontColor: "#7c2d12",
      cardBackgroundColor: "#fffaf0",
      background: "radial-gradient(circle at top, #fef9c3 0%, #fde68a 42%, #fb923c 100%)",
      backgroundAnnouncements: "#ffedd5",
      backgroundClock: "#fed7aa",
      surfaceShadow: "raised",
      agendaActiveStepBackgroundColor: "#f59e0b",
      agendaActiveStepColor: "#451a03",
      agendaActiveSubStepColor: "#451a03",
      agendaProgressFillColor: "#fbbf24",
      clockFontStyle: "Nunito, sans-serif",
      clockFontColor: "#9a3412",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#9a3412",
      reminderFontStyle: "Nunito, sans-serif",
      countdownBackgroundColor: "#ffedd5",
      countdownFontColor: "#9a3412",
      countdownFontStyle: "Nunito, sans-serif",
    },
  },
  {
    id: "clear-sky-glass",
    label: "Clear Sky Glass",
    styleDefaults: buildGlassPresetStyleDefaults({
      background: "#dbeafe",
      defaultFontColor: "#0c4a6e",
      defaultFontStyle: "Poppins, sans-serif",
      agendaActiveStepBackgroundColor: "#1d4ed8",
      agendaActiveStepColor: "#ffffff",
      agendaSubStepColor: "#1e3a8a",
      agendaProgressTrackColor: "rgba(12, 74, 110, 0.2)",
      reminderBackgroundColor: "#ffffff",
      reminderFontColor: "#0c4a6e",
      reminderOutlineColor: "#1d4ed8",
      countdownBackgroundColor: "#eff6ff",
      countdownFontColor: "#1e3a8a",
      countdownOutlineColor: "#2563eb",
    }),
  },
  {
    id: "midnight-glass",
    label: "Midnight Glass",
    styleDefaults: buildGlassPresetStyleDefaults({
      background: "#0f172a",
      defaultFontColor: "#e2e8f0",
      defaultFontStyle: "Roboto, sans-serif",
      agendaActiveStepBackgroundColor: "#38bdf8",
      agendaActiveStepColor: "#0f172a",
      agendaSubStepColor: "#94a3b8",
      agendaProgressTrackColor: "rgba(226, 232, 240, 0.22)",
      reminderBackgroundColor: "#1e293b",
      reminderFontColor: "#f8fafc",
      reminderOutlineColor: "#38bdf8",
      countdownBackgroundColor: "#334155",
      countdownFontColor: "#e0f2fe",
      countdownOutlineColor: "#7dd3fc",
    }),
  },
  {
    id: "golden-hour-glass",
    label: "Golden Hour Glass",
    styleDefaults: buildGlassPresetStyleDefaults({
      background: "linear-gradient(145deg, #fff7ed 0%, #fdba74 48%, #fb923c 100%)",
      defaultFontColor: "#7c2d12",
      defaultFontStyle: "Nunito, sans-serif",
      agendaActiveStepBackgroundColor: "#ea580c",
      agendaActiveStepColor: "#fff7ed",
      agendaSubStepColor: "#9a3412",
      agendaProgressTrackColor: "rgba(124, 45, 18, 0.22)",
      reminderBackgroundColor: "#fff7ed",
      reminderFontColor: "#7c2d12",
      reminderOutlineColor: "#c2410c",
      countdownBackgroundColor: "#ffedd5",
      countdownFontColor: "#9a3412",
      countdownOutlineColor: "#ea580c",
      surfaceCornerRadius: 18,
    }),
  },
  {
    id: "emerald-mist-glass",
    label: "Emerald Mist Glass",
    styleDefaults: buildGlassPresetStyleDefaults({
      background: "linear-gradient(140deg, #ecfdf5 0%, #a7f3d0 45%, #6ee7b7 100%)",
      defaultFontColor: "#064e3b",
      defaultFontStyle: "Nunito, sans-serif",
      agendaActiveStepBackgroundColor: "#047857",
      agendaActiveStepColor: "#ecfdf5",
      agendaSubStepColor: "#065f46",
      agendaProgressTrackColor: "rgba(6, 78, 59, 0.2)",
      reminderBackgroundColor: "#ffffff",
      reminderFontColor: "#064e3b",
      reminderOutlineColor: "#059669",
      countdownBackgroundColor: "#d1fae5",
      countdownFontColor: "#065f46",
      countdownOutlineColor: "#047857",
      surfaceCornerRadius: 18,
    }),
  },
];

export const CLASS_STYLE_PRESET_SCHEMES = RAW_CLASS_STYLE_PRESET_SCHEMES.map((presetScheme) => ({
  ...presetScheme,
  matchStyleDefaults: getPresetMatchStyleDefaults(presetScheme.styleDefaults),
  styleDefaults: buildCompletePresetStyleDefaults(presetScheme.id, presetScheme.styleDefaults),
}));

export const pickFirstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const SURFACE_OUTLINE_STYLE_OPTIONS = ["solid", "dashed", "dotted"];

const clampNumber = (value, min, max, fallback) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, numericValue));
};

const normalizeHexColor = (value, fallback) =>
  typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())
    ? value.trim()
    : fallback;

const hexToRgb = (hexColor) => {
  const normalized = hexColor.replace("#", "");
  const expanded = normalized.length === 3
    ? normalized.split("").map((character) => `${character}${character}`).join("")
    : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

export const normalizeCardBackgroundOpacity = (value) =>
  clampNumber(value, 0, 1, 1);

export const normalizeSurfaceBackgroundOpacity = (value) =>
  clampNumber(value, 0, 1, 1);

/**
 * Solid content-card paint from class style (hex + optional opacity). Non-hex values fall back to solid color only.
 */
export const resolveClassStyleCardBackgroundStyle = (styleDefaults = {}) => {
  const raw = styleDefaults?.cardBackgroundColor;
  const opacity = normalizeCardBackgroundOpacity(styleDefaults?.cardBackgroundOpacity);

  if (typeof raw === "string" && raw.trim().startsWith("#") && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw.trim())) {
    const hex = normalizeHexColor(raw, "#ffffff");
    if (opacity >= 0.999) {
      return { backgroundColor: hex };
    }
    const { r, g, b } = hexToRgb(hex);
    return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})` };
  }

  if (typeof raw === "string" && raw === "transparent") {
    return { backgroundColor: "transparent" };
  }

  return {
    backgroundColor: typeof raw === "string" && raw ? raw : "#ffffff",
  };
};

export const isClassStyleCardBackgroundSolidHex = (value) =>
  typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());

/**
 * Effective solid hex for card/surface backgrounds in the style editor.
 * Matches the color picker display: non-hex values like `transparent` show as
 * the fallback (usually white). Images/gradients cannot take opacity.
 */
export const resolveEffectiveClassStyleSolidHex = (value, fallback = "#ffffff") => {
  if (isClassStyleCardBackgroundSolidHex(value)) {
    return value.trim();
  }

  if (isImageBackgroundValue(value) || isGradientBackground(value)) {
    return null;
  }

  const safeFallback = typeof fallback === "string" && fallback.trim().startsWith("#")
    ? fallback.trim()
    : "#ffffff";

  return isClassStyleCardBackgroundSolidHex(safeFallback) ? safeFallback : "#ffffff";
};

export const canEditClassStyleCardBackgroundOpacity = (value) =>
  resolveEffectiveClassStyleSolidHex(value) != null;

/**
 * When opacity changes on a non-hex background (e.g. transparent glass presets),
 * materialize the picker-visible solid hex so opacity can actually render.
 */
export const buildClassStyleBackgroundOpacityUpdate = ({
  colorField,
  opacityField,
  colorValue,
  nextOpacity,
  fallbackHex = "#ffffff",
}) => {
  const opacity = normalizeCardBackgroundOpacity(nextOpacity);
  const effectiveHex = resolveEffectiveClassStyleSolidHex(colorValue, fallbackHex);

  if (!effectiveHex) {
    return null;
  }

  if (isClassStyleCardBackgroundSolidHex(colorValue)) {
    return { [opacityField]: opacity };
  }

  return {
    [colorField]: effectiveHex,
    [opacityField]: opacity,
  };
};

const STYLE_HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const normalizeStyleHexColor = (value) => {
  if (typeof value !== "string" || !value.startsWith("#")) return null;
  const trimmed = value.trim();
  if (!STYLE_HEX_COLOR_PATTERN.test(trimmed)) return null;

  if (trimmed.length === 4) {
    const [, red, green, blue] = trimmed;
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }

  return trimmed.toUpperCase();
};

export const collectClassStyleHexColors = (styleDefaults = {}) => {
  const colors = new Set();

  const visit = (node) => {
    if (node == null) return;

    if (typeof node === "string") {
      const normalized = normalizeStyleHexColor(node);
      if (normalized) colors.add(normalized);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (typeof node === "object") {
      Object.values(node).forEach(visit);
    }
  };

  visit(styleDefaults);
  return Array.from(colors);
};

export const getDefaultSurfaceShadowCustom = () => ({
  offsetX: 0,
  offsetY: 12,
  blur: 24,
  spread: 0,
  color: "#0f172a",
  opacity: 0.24,
});

export const getDefaultSurfaceOutlineWidth = () => 0;

export const getDefaultSurfaceOutlineColor = () => "#94a3b8";

export const getDefaultSurfaceOutlineStyle = () => "solid";

export const getDefaultSurfaceCornerRadius = () => 16;

export const normalizeSurfaceShadowCustom = (value = {}) => {
  const fallback = getDefaultSurfaceShadowCustom();
  return {
    offsetX: clampNumber(value?.offsetX, -80, 80, fallback.offsetX),
    offsetY: clampNumber(value?.offsetY, -80, 80, fallback.offsetY),
    blur: clampNumber(value?.blur, 0, 120, fallback.blur),
    spread: clampNumber(value?.spread, -40, 40, fallback.spread),
    color: normalizeHexColor(value?.color, fallback.color),
    opacity: clampNumber(value?.opacity, 0, 1, fallback.opacity),
  };
};

export const normalizeSurfaceOutlineStyle = (value) =>
  SURFACE_OUTLINE_STYLE_OPTIONS.includes(value)
    ? value
    : getDefaultSurfaceOutlineStyle();

export const normalizeSurfaceOutlineWidth = (value) =>
  clampNumber(value, 0, 12, getDefaultSurfaceOutlineWidth());

export const normalizeSurfaceCornerRadius = (value) =>
  clampNumber(value, 0, 48, getDefaultSurfaceCornerRadius());

export const normalizeSurfaceOutlineColor = (value) =>
  normalizeHexColor(value, getDefaultSurfaceOutlineColor());

export const getClassStyleShadowValue = (shadowPreset, customShadow = null) => {
  if (shadowPreset === "custom") {
    const normalizedCustomShadow = normalizeSurfaceShadowCustom(customShadow || {});
    const { r, g, b } = hexToRgb(normalizedCustomShadow.color);
    return `${normalizedCustomShadow.offsetX}px ${normalizedCustomShadow.offsetY}px ${normalizedCustomShadow.blur}px ${normalizedCustomShadow.spread}px rgba(${r}, ${g}, ${b}, ${normalizedCustomShadow.opacity})`;
  }

  return CLASS_STYLE_SHADOW_PRESETS.find((preset) => preset.id === shadowPreset)?.boxShadow || "none";
};

/**
 * Mini scheme mockups are ~180px tall; full Present surfaces use the same shadow strings
 * at ~4–5× scale, so we shrink px lengths to keep depth proportional.
 */
export const CLASS_STYLE_MINI_MOCKUP_PX_SCALE = 0.37;

export const scaleClassStyleCssPxLengths = (value, scale = CLASS_STYLE_MINI_MOCKUP_PX_SCALE) => {
  if (typeof value !== "string" || !value.trim() || value.trim() === "none") {
    return value;
  }
  return value.replace(/\b(-?\d+(?:\.\d+)?)px\b/g, (match, raw) => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n === 0) {
      return "0px";
    }
    const scaled = n * scale;
    const rounded = Math.round(scaled * 100) / 100;
    return `${rounded}px`;
  });
};

export const getClassStyleSurfaceChrome = (styleDefaults = {}) => {
  const outlineWidth = normalizeSurfaceOutlineWidth(styleDefaults.surfaceOutlineWidth);
  const outlineColor = normalizeSurfaceOutlineColor(styleDefaults.surfaceOutlineColor);
  const outlineStyle = normalizeSurfaceOutlineStyle(styleDefaults.surfaceOutlineStyle);
  const cornerRadius = normalizeSurfaceCornerRadius(styleDefaults.surfaceCornerRadius);

  return {
    border: outlineWidth > 0 ? `${outlineWidth}px ${outlineStyle} ${outlineColor}` : "none",
    borderRadius: `${cornerRadius}px`,
  };
};

/** Surfaces that can each have their own shadow + outline (falls back to legacy global `surface*` keys). */
export const CLASS_STYLE_SURFACE_IDS = ["marquee", "clock", "content", "agenda", "reminder", "countdown"];

export const getPerSurfaceClassStyleFieldKeys = (surfaceId) => ({
  shadow: `${surfaceId}SurfaceShadow`,
  shadowCustom: `${surfaceId}SurfaceShadowCustom`,
  outlineWidth: `${surfaceId}SurfaceOutlineWidth`,
  outlineColor: `${surfaceId}SurfaceOutlineColor`,
  outlineStyle: `${surfaceId}SurfaceOutlineStyle`,
  cornerRadius: `${surfaceId}SurfaceCornerRadius`,
});

const CLASS_STYLE_EXTENDED_PRESERVE_FIELDS = [
  "disableSurfaceInsetRim",
  "backgroundClassInfo",
  "backgroundClassInfoOpacity",
  "classInfoFontColor",
  "classInfoFontStyle",
  "classInfoLabelFontColor",
  "classStylePresetId",
  "classStyleCustomSchemeId",
  "contentSurfaceShadow",
  "contentSurfaceShadowCustom",
  "contentSurfaceOutlineWidth",
  "contentSurfaceOutlineColor",
  "contentSurfaceOutlineStyle",
  "contentSurfaceCornerRadius",
];

/** Fields not covered by the core normalize map (glass presets, per-surface chrome, scheme ids). */
export const preserveClassStyleExtensions = (savedDefaults = {}) => {
  const preserved = {};

  CLASS_STYLE_EXTENDED_PRESERVE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(savedDefaults, field)) {
      preserved[field] = savedDefaults[field];
    }
  });

  CLASS_STYLE_SURFACE_IDS.forEach((surfaceId) => {
    const keys = getPerSurfaceClassStyleFieldKeys(surfaceId);
    Object.values(keys).forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(savedDefaults, field)) {
        preserved[field] = savedDefaults[field];
      }
    });
  });

  return preserved;
};

export const DEFAULT_CLASS_STYLE_PRESET_ID = "lesson-launchpad-default";

export const getClassStylePresetSchemeById = (presetId) =>
  CLASS_STYLE_PRESET_SCHEMES.find((presetScheme) => presetScheme.id === presetId) || null;

export const attachClassStylePresetId = (styleDefaults = {}, presetId) => ({
  ...styleDefaults,
  classStylePresetId: presetId || null,
  classStyleCustomSchemeId: null,
});

export const attachClassStyleCustomSchemeId = (styleDefaults = {}, customSchemeId) => ({
  ...styleDefaults,
  classStylePresetId: null,
  classStyleCustomSchemeId: customSchemeId || null,
});

export const buildDefaultPresetClassStyleDefaults = (userOrSettings = {}, classItem = {}) => {
  const defaultPreset = getClassStylePresetSchemeById(DEFAULT_CLASS_STYLE_PRESET_ID);
  const userSettings = userOrSettings?.settings ? userOrSettings.settings : userOrSettings;
  const base = getDefaultClassStyleDefaults(userSettings);
  return normalizeClassStyleDefaults(
    {
      ...classItem,
      classStyleDefaults: attachClassStylePresetId(
        { ...base, ...(defaultPreset?.styleDefaults || {}) },
        DEFAULT_CLASS_STYLE_PRESET_ID
      ),
    },
    userOrSettings,
    { preserveLegacyImageFallback: false }
  );
};

export const clearClassStyleSchemeIds = (styleDefaults = {}) => ({
  ...styleDefaults,
  classStylePresetId: null,
  classStyleCustomSchemeId: null,
});

export const resolveAppliedClassStylePresetId = (styleDefaults = {}) => {
  const presetId = styleDefaults?.classStylePresetId;
  if (presetId && getClassStylePresetSchemeById(presetId)) {
    return presetId;
  }

  const presetMatch = CLASS_STYLE_PRESET_SCHEMES.find((presetScheme) =>
    doesStyleMatchPresetScheme(styleDefaults, presetScheme)
  );
  return presetMatch?.id || null;
};

export const resolveAppliedClassStyleCustomSchemeId = (styleDefaults = {}, customSchemes = []) => {
  const customId = styleDefaults?.classStyleCustomSchemeId;
  if (customId && customSchemes.some((scheme) => scheme.id === customId)) {
    return customId;
  }

  return customSchemes.find((scheme) => doesStyleMatchPresetScheme(styleDefaults, scheme))?.id || null;
};

export const classReferencesCustomStyleScheme = (classItem, schemeId, user = {}) => {
  const targetId = String(schemeId || "").trim();
  if (!targetId || !classItem) {
    return false;
  }

  const normalized = normalizeClassStyleDefaults(classItem, user);
  return String(normalized.classStyleCustomSchemeId || "").trim() === targetId;
};

export const getClassStyleSurfaceChromeFor = (styleDefaults = {}, surfaceId, options = {}) => {
  const k = getPerSurfaceClassStyleFieldKeys(surfaceId);
  const outlineWidth = normalizeSurfaceOutlineWidth(
    pickFirstDefined(styleDefaults[k.outlineWidth], styleDefaults.surfaceOutlineWidth)
  );
  const outlineColor = normalizeSurfaceOutlineColor(
    pickFirstDefined(styleDefaults[k.outlineColor], styleDefaults.surfaceOutlineColor)
  );
  const outlineStyle = normalizeSurfaceOutlineStyle(
    pickFirstDefined(styleDefaults[k.outlineStyle], styleDefaults.surfaceOutlineStyle)
  );
  const cornerRadius = normalizeSurfaceCornerRadius(
    pickFirstDefined(styleDefaults[k.cornerRadius], styleDefaults.surfaceCornerRadius)
  );

  const forMini = Boolean(options.forMiniMockup);
  const s = CLASS_STYLE_MINI_MOCKUP_PX_SCALE;
  const scaledOutlineWidth =
    forMini && outlineWidth > 0 ? Math.max(1, Math.round(outlineWidth * s)) : outlineWidth;
  const scaledCornerRadius =
    forMini && cornerRadius > 0 ? Math.max(1, Math.round(cornerRadius * s)) : cornerRadius;

  return {
    border: scaledOutlineWidth > 0 ? `${scaledOutlineWidth}px ${outlineStyle} ${outlineColor}` : "none",
    borderRadius: `${scaledCornerRadius}px`,
  };
};

export const getClassStyleShadowForSurface = (styleDefaults = {}, surfaceId, options = {}) => {
  const k = getPerSurfaceClassStyleFieldKeys(surfaceId);
  const shadowPreset = pickFirstDefined(styleDefaults[k.shadow], styleDefaults.surfaceShadow);
  const customShadow = pickFirstDefined(styleDefaults[k.shadowCustom], styleDefaults.surfaceShadowCustom);
  const raw = getClassStyleShadowValue(shadowPreset, customShadow);
  return options.forMiniMockup ? scaleClassStyleCssPxLengths(raw) : raw;
};

/** Drop shadow plus subtle inner rim (used for content cards, reminder, and countdown previews). */
export const getClassStyleSurfaceCompositeShadowWithInsetRim = (styleDefaults = {}, surfaceId, options = {}) => {
  const drop = getClassStyleShadowForSurface(styleDefaults, surfaceId, options);
  if (drop === "none" && styleDefaults.disableSurfaceInsetRim) {
    return "none";
  }
  const rim = "inset 0 0 0 1px rgba(15, 23, 42, 0.08)";
  const rimScaled = options.forMiniMockup ? scaleClassStyleCssPxLengths(rim) : rim;
  return drop === "none" ? rimScaled : `${drop}, ${rimScaled}`;
};

export const getClassStyleContentCardCompositeShadow = (styleDefaults = {}, options = {}) =>
  getClassStyleSurfaceCompositeShadowWithInsetRim(styleDefaults, "content", options);

export const getClassStyleAgendaCardCompositeShadow = (styleDefaults = {}, options = {}) =>
  getClassStyleSurfaceCompositeShadowWithInsetRim(styleDefaults, "agenda", options);

const pickBackgroundValue = (savedValue, legacyValue, fallbackValue, options = {}) => {
  if (savedValue !== undefined && savedValue !== null) {
    if (options.preserveLegacyImageFallback !== false && savedValue === fallbackValue && isImageBackgroundValue(legacyValue)) {
      return legacyValue;
    }
    return savedValue;
  }

  return pickFirstDefined(legacyValue, fallbackValue);
};

const getDefaultClockFormat = (clockLanguage) =>
  (clockLanguage === "French" || clockLanguage === "German" || clockLanguage === "Spanish")
    ? "24h"
    : "12hr";

export const getDefaultClassStyleDefaults = (userSettings = {}) => ({
  defaultFontStyle: userSettings?.defaultFontStyle || "Helvetica",
  defaultFontSize: userSettings?.defaultFontSize || "20pt",
  defaultFontColor: "#0f172a",
  cardBackgroundColor: "#ffffff",
  cardBackgroundOpacity: 1,
  ...getDefaultBackgroundImageSettings(),
  backgroundAnnouncementsOpacity: 1,
  backgroundClockOpacity: 1,
  reminderBackgroundColor: "#ffffff",
  reminderFontColor: "#0f172a",
  reminderFontStyle: userSettings?.defaultFontStyle || "Helvetica",
  countdownBackgroundColor: "#ffffff",
  countdownMessageFontColor: "#0f172a",
  countdownMessageFontStyle: userSettings?.defaultFontStyle || "Helvetica",
  countdownFontColor: "#0f172a",
  countdownFontStyle: userSettings?.defaultFontStyle || "Helvetica",
  agendaBackgroundColor: "#ffffff",
  agendaBackgroundOpacity: 1,
  agendaTitleFont: userSettings?.defaultFontStyle || "Helvetica",
  agendaTitleColor: "#0f172a",
  agendaStepFont: userSettings?.defaultFontStyle || "Helvetica",
  agendaStepColor: "#0f172a",
  agendaSubStepFont: userSettings?.defaultFontStyle || "Helvetica",
  agendaSubStepColor: "#475467",
  agendaActiveStepBackgroundColor: "#004aad",
  agendaActiveStepColor: "#ffffff",
  agendaActiveSubStepColor: "#ffffff",
  agendaProgressTrackColor: "rgba(255, 255, 255, 0.32)",
  agendaProgressFillColor: "rgba(255, 255, 255, 0.82)",
  agendaProgressScrubberColor: "#ffffff",
  background: "#f8fafc",
  backgroundAnnouncements: "#ffffff",
  backgroundClock: "#ffffff",
  surfaceShadow: "soft",
  surfaceShadowCustom: getDefaultSurfaceShadowCustom(),
  surfaceOutlineWidth: getDefaultSurfaceOutlineWidth(),
  surfaceOutlineColor: getDefaultSurfaceOutlineColor(),
  surfaceOutlineStyle: getDefaultSurfaceOutlineStyle(),
  surfaceCornerRadius: getDefaultSurfaceCornerRadius(),
  clockFontStyle: userSettings?.defaultFontStyle || "Helvetica",
  clockFontColor: "#0f172a",
  clockLanguage: "English",
  clockFormat: getDefaultClockFormat("English"),
});

const getContentBoxStylePresetId = (value) =>
  CONTENT_BOX_STYLE_PRESET_IDS.includes(value) ? value : CONTENT_BOX_STYLE_PRESET_IDS[0];

export const getDefaultContentBoxStylePresetId = (styleDefaults = {}) =>
  getContentBoxStylePresetId(styleDefaults?.defaultContentBoxStylePresetId);

export const getContentBoxStylePresetLabel = (presetId) =>
  CONTENT_BOX_STYLE_PRESET_OPTIONS.find((option) => option.id === presetId)?.label || CONTENT_BOX_STYLE_PRESET_OPTIONS[0].label;

export const CONTENT_BOX_PRESET_TYPOGRAPHY_FIELDS = [
  "defaultFontStyle",
  "defaultFontSize",
  "defaultFontColor",
];

export const CONTENT_BOX_PRESET_LEGACY_ROOT_MIRROR_FIELDS = [
  "cardBackgroundColor",
  "cardBackgroundOpacity",
  "contentSurfaceShadow",
  "contentSurfaceShadowCustom",
  "contentSurfaceOutlineWidth",
  "contentSurfaceOutlineColor",
  "contentSurfaceOutlineStyle",
  "contentSurfaceCornerRadius",
];

export const CONTENT_BOX_PRESET_STYLE_FIELDS = [
  ...CONTENT_BOX_PRESET_TYPOGRAPHY_FIELDS,
  ...CONTENT_BOX_PRESET_LEGACY_ROOT_MIRROR_FIELDS,
];

export const getLegacyContentBoxStylePresetFromDefaults = (styleDefaults = {}) => ({
  defaultFontStyle: pickFirstDefined(styleDefaults.defaultFontStyle, "Helvetica"),
  defaultFontSize: pickFirstDefined(styleDefaults.defaultFontSize, "20pt"),
  defaultFontColor: pickFirstDefined(styleDefaults.defaultFontColor, "#0f172a"),
  cardBackgroundColor: pickFirstDefined(styleDefaults.cardBackgroundColor, "#ffffff"),
  cardBackgroundOpacity: normalizeCardBackgroundOpacity(styleDefaults.cardBackgroundOpacity),
  contentSurfaceShadow: pickFirstDefined(styleDefaults.contentSurfaceShadow, styleDefaults.surfaceShadow, "none"),
  contentSurfaceShadowCustom: normalizeSurfaceShadowCustom(
    pickFirstDefined(styleDefaults.contentSurfaceShadowCustom, styleDefaults.surfaceShadowCustom, getDefaultSurfaceShadowCustom())
  ),
  contentSurfaceOutlineWidth: normalizeSurfaceOutlineWidth(
    pickFirstDefined(styleDefaults.contentSurfaceOutlineWidth, styleDefaults.surfaceOutlineWidth)
  ),
  contentSurfaceOutlineColor: normalizeSurfaceOutlineColor(
    pickFirstDefined(styleDefaults.contentSurfaceOutlineColor, styleDefaults.surfaceOutlineColor)
  ),
  contentSurfaceOutlineStyle: normalizeSurfaceOutlineStyle(
    pickFirstDefined(styleDefaults.contentSurfaceOutlineStyle, styleDefaults.surfaceOutlineStyle)
  ),
  contentSurfaceCornerRadius: normalizeSurfaceCornerRadius(
    pickFirstDefined(styleDefaults.contentSurfaceCornerRadius, styleDefaults.surfaceCornerRadius)
  ),
});

export const normalizeContentBoxStylePreset = (preset = {}, fallbackPreset = {}) => ({
  defaultFontStyle: pickFirstDefined(preset.defaultFontStyle, fallbackPreset.defaultFontStyle, "Helvetica"),
  defaultFontSize: pickFirstDefined(preset.defaultFontSize, fallbackPreset.defaultFontSize, "20pt"),
  defaultFontColor: pickFirstDefined(preset.defaultFontColor, fallbackPreset.defaultFontColor, "#0f172a"),
  cardBackgroundColor: pickFirstDefined(preset.cardBackgroundColor, fallbackPreset.cardBackgroundColor, "#ffffff"),
  cardBackgroundOpacity: normalizeCardBackgroundOpacity(
    pickFirstDefined(preset.cardBackgroundOpacity, fallbackPreset.cardBackgroundOpacity)
  ),
  contentSurfaceShadow: pickFirstDefined(preset.contentSurfaceShadow, fallbackPreset.contentSurfaceShadow, "none"),
  contentSurfaceShadowCustom: normalizeSurfaceShadowCustom(
    pickFirstDefined(preset.contentSurfaceShadowCustom, fallbackPreset.contentSurfaceShadowCustom, getDefaultSurfaceShadowCustom())
  ),
  contentSurfaceOutlineWidth: normalizeSurfaceOutlineWidth(
    pickFirstDefined(preset.contentSurfaceOutlineWidth, fallbackPreset.contentSurfaceOutlineWidth)
  ),
  contentSurfaceOutlineColor: normalizeSurfaceOutlineColor(
    pickFirstDefined(preset.contentSurfaceOutlineColor, fallbackPreset.contentSurfaceOutlineColor)
  ),
  contentSurfaceOutlineStyle: normalizeSurfaceOutlineStyle(
    pickFirstDefined(preset.contentSurfaceOutlineStyle, fallbackPreset.contentSurfaceOutlineStyle)
  ),
  contentSurfaceCornerRadius: normalizeSurfaceCornerRadius(
    pickFirstDefined(preset.contentSurfaceCornerRadius, fallbackPreset.contentSurfaceCornerRadius)
  ),
});

export const normalizeContentBoxStylePresets = (presets = {}, fallbackPreset = {}) => {
  const sourceById = Array.isArray(presets)
    ? presets.reduce((accumulator, preset, index) => {
        const id = getContentBoxStylePresetId(preset?.id || CONTENT_BOX_STYLE_PRESET_IDS[index]);
        accumulator[id] = preset;
        return accumulator;
      }, {})
    : presets;

  return CONTENT_BOX_STYLE_PRESET_OPTIONS.map((option) => ({
    id: option.id,
    label: option.label,
    ...normalizeContentBoxStylePreset(sourceById?.[option.id], fallbackPreset),
  }));
};

export const getContentBoxStylePreset = (styleDefaults = {}, presetId = null) => {
  const fallbackPreset = getLegacyContentBoxStylePresetFromDefaults(styleDefaults);
  const normalizedPresets = normalizeContentBoxStylePresets(styleDefaults.contentBoxStylePresets, fallbackPreset);
  const selectedPresetId = getContentBoxStylePresetId(presetId || styleDefaults.defaultContentBoxStylePresetId);
  return normalizedPresets.find((preset) => preset.id === selectedPresetId) || normalizedPresets[0];
};

export const getClassStyleDefaultsForContentBoxPreset = (styleDefaults = {}, presetId = null) => ({
  ...styleDefaults,
  ...getContentBoxStylePreset(styleDefaults, presetId),
});

export const normalizeClassStyleDefaults = (classItem = {}, userOrSettings = {}, options = {}) => {
  const userSettings = userOrSettings?.settings ? userOrSettings.settings : userOrSettings;
  const fallbackDefaults = getDefaultClassStyleDefaults(userSettings);
  const savedDefaults = classItem?.classStyleDefaults || {};
  const clockLanguage = pickFirstDefined(
    savedDefaults.clockLanguage,
    classItem?.clockLanguage,
    fallbackDefaults.clockLanguage
  );

  const normalized = {
    ...fallbackDefaults,
    ...savedDefaults,
    ...normalizeBackgroundImageSettings({
      ...fallbackDefaults,
      ...savedDefaults,
    }),
    defaultFontStyle: pickFirstDefined(
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.defaultFontStyle
    ),
    defaultFontSize: pickFirstDefined(
      savedDefaults.defaultFontSize,
      userSettings?.defaultFontSize,
      fallbackDefaults.defaultFontSize
    ),
    defaultFontColor: pickFirstDefined(
      savedDefaults.defaultFontColor,
      fallbackDefaults.defaultFontColor
    ),
    cardBackgroundColor: pickFirstDefined(
      savedDefaults.cardBackgroundColor,
      fallbackDefaults.cardBackgroundColor
    ),
    cardBackgroundOpacity: normalizeCardBackgroundOpacity(
      pickFirstDefined(
        savedDefaults.cardBackgroundOpacity,
        fallbackDefaults.cardBackgroundOpacity
      )
    ),
    backgroundAnnouncementsOpacity: normalizeSurfaceBackgroundOpacity(
      pickFirstDefined(
        savedDefaults.backgroundAnnouncementsOpacity,
        fallbackDefaults.backgroundAnnouncementsOpacity
      )
    ),
    backgroundClockOpacity: normalizeSurfaceBackgroundOpacity(
      pickFirstDefined(
        savedDefaults.backgroundClockOpacity,
        fallbackDefaults.backgroundClockOpacity
      )
    ),
    reminderBackgroundColor: pickFirstDefined(
      savedDefaults.reminderBackgroundColor,
      fallbackDefaults.reminderBackgroundColor
    ),
    reminderFontColor: pickFirstDefined(
      savedDefaults.reminderFontColor,
      fallbackDefaults.reminderFontColor
    ),
    reminderFontStyle: pickFirstDefined(
      savedDefaults.reminderFontStyle,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.reminderFontStyle
    ),
    countdownBackgroundColor: pickFirstDefined(
      savedDefaults.countdownBackgroundColor,
      fallbackDefaults.countdownBackgroundColor
    ),
    countdownMessageFontColor: pickFirstDefined(
      savedDefaults.countdownMessageFontColor,
      savedDefaults.countdownFontColor,
      fallbackDefaults.countdownMessageFontColor
    ),
    countdownMessageFontStyle: pickFirstDefined(
      savedDefaults.countdownMessageFontStyle,
      savedDefaults.countdownFontStyle,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.countdownMessageFontStyle
    ),
    countdownFontColor: pickFirstDefined(
      savedDefaults.countdownFontColor,
      fallbackDefaults.countdownFontColor
    ),
    countdownFontStyle: pickFirstDefined(
      savedDefaults.countdownFontStyle,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.countdownFontStyle
    ),
    agendaBackgroundColor: pickFirstDefined(
      savedDefaults.agendaBackgroundColor,
      fallbackDefaults.agendaBackgroundColor
    ),
    agendaBackgroundOpacity: normalizeCardBackgroundOpacity(
      pickFirstDefined(
        savedDefaults.agendaBackgroundOpacity,
        fallbackDefaults.agendaBackgroundOpacity
      )
    ),
    agendaTitleFont: pickFirstDefined(
      savedDefaults.agendaTitleFont,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.agendaTitleFont
    ),
    agendaTitleColor: pickFirstDefined(
      savedDefaults.agendaTitleColor,
      fallbackDefaults.agendaTitleColor
    ),
    agendaStepFont: pickFirstDefined(
      savedDefaults.agendaStepFont,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.agendaStepFont
    ),
    agendaStepColor: pickFirstDefined(
      savedDefaults.agendaStepColor,
      fallbackDefaults.agendaStepColor
    ),
    agendaSubStepFont: pickFirstDefined(
      savedDefaults.agendaSubStepFont,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.agendaSubStepFont
    ),
    agendaSubStepColor: pickFirstDefined(
      savedDefaults.agendaSubStepColor,
      fallbackDefaults.agendaSubStepColor
    ),
    agendaActiveStepBackgroundColor: pickFirstDefined(
      savedDefaults.agendaActiveStepBackgroundColor,
      fallbackDefaults.agendaActiveStepBackgroundColor
    ),
    agendaActiveStepColor: pickFirstDefined(
      savedDefaults.agendaActiveStepColor,
      fallbackDefaults.agendaActiveStepColor
    ),
    agendaActiveSubStepColor: pickFirstDefined(
      savedDefaults.agendaActiveSubStepColor,
      savedDefaults.agendaActiveStepColor,
      fallbackDefaults.agendaActiveSubStepColor
    ),
    agendaProgressTrackColor: pickFirstDefined(
      savedDefaults.agendaProgressTrackColor,
      fallbackDefaults.agendaProgressTrackColor
    ),
    agendaProgressFillColor: pickFirstDefined(
      savedDefaults.agendaProgressFillColor,
      fallbackDefaults.agendaProgressFillColor
    ),
    agendaProgressScrubberColor: pickFirstDefined(
      savedDefaults.agendaProgressScrubberColor,
      fallbackDefaults.agendaProgressScrubberColor
    ),
    background: pickBackgroundValue(
      savedDefaults.background,
      classItem?.background,
      fallbackDefaults.background,
      options
    ),
    backgroundAnnouncements: pickBackgroundValue(
      savedDefaults.backgroundAnnouncements,
      classItem?.backgroundAnnouncements,
      fallbackDefaults.backgroundAnnouncements,
      options
    ),
    backgroundClock: pickBackgroundValue(
      savedDefaults.backgroundClock,
      classItem?.backgroundClock,
      fallbackDefaults.backgroundClock,
      options
    ),
    surfaceShadow: pickFirstDefined(
      savedDefaults.surfaceShadow,
      fallbackDefaults.surfaceShadow
    ),
    surfaceShadowCustom: normalizeSurfaceShadowCustom(
      pickFirstDefined(
        savedDefaults.surfaceShadowCustom,
        fallbackDefaults.surfaceShadowCustom
      )
    ),
    surfaceOutlineWidth: normalizeSurfaceOutlineWidth(
      pickFirstDefined(
        savedDefaults.surfaceOutlineWidth,
        fallbackDefaults.surfaceOutlineWidth
      )
    ),
    surfaceOutlineColor: normalizeSurfaceOutlineColor(
      pickFirstDefined(
        savedDefaults.surfaceOutlineColor,
        fallbackDefaults.surfaceOutlineColor
      )
    ),
    surfaceOutlineStyle: normalizeSurfaceOutlineStyle(
      pickFirstDefined(
        savedDefaults.surfaceOutlineStyle,
        fallbackDefaults.surfaceOutlineStyle
      )
    ),
    surfaceCornerRadius: normalizeSurfaceCornerRadius(
      pickFirstDefined(
        savedDefaults.surfaceCornerRadius,
        fallbackDefaults.surfaceCornerRadius
      )
    ),
    clockFontStyle: pickFirstDefined(
      savedDefaults.clockFontStyle,
      savedDefaults.defaultFontStyle,
      userSettings?.defaultFontStyle,
      fallbackDefaults.clockFontStyle
    ),
    clockFontColor: pickFirstDefined(
      savedDefaults.clockFontColor,
      classItem?.clockFontColor,
      fallbackDefaults.clockFontColor
    ),
    clockLanguage,
    clockFormat: pickFirstDefined(
      savedDefaults.clockFormat,
      classItem?.clockFormat,
      getDefaultClockFormat(clockLanguage)
    ),
  };

  const legacyContentBoxPreset = getLegacyContentBoxStylePresetFromDefaults(normalized);
  const result = {
    ...normalized,
    ...preserveClassStyleExtensions(savedDefaults),
    defaultContentBoxStylePresetId: getContentBoxStylePresetId(savedDefaults.defaultContentBoxStylePresetId),
    contentBoxStylePresets: normalizeContentBoxStylePresets(savedDefaults.contentBoxStylePresets, legacyContentBoxPreset),
  };
  delete result.clockVersion;
  return result;
};

/**
 * Deep value compare for class style fields (primitives + plain nested objects/arrays).
 * Used so preset/custom scheme matching survives separate `normalizeClassStyleDefaults` runs
 * (e.g. `surfaceShadowCustom` is a new object reference with the same contents).
 */
export const classStyleSchemeFieldEqual = (a, b) => {
  if (Object.is(a, b)) {
    return true;
  }
  if (a == null && b == null) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (typeof a !== "object" || typeof b !== "object") {
    return a === b;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((entry, index) => classStyleSchemeFieldEqual(entry, b[index]));
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every((key) => Object.prototype.hasOwnProperty.call(b, key) && classStyleSchemeFieldEqual(a[key], b[key]));
};

export const classStyleNormalizedMapsEqual = (a, b) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if (!classStyleSchemeFieldEqual(a[key], b[key])) {
      return false;
    }
  }
  return true;
};

export const doesStyleMatchPresetScheme = (styleDefaults, presetScheme) => {
  const matchDefaults = presetScheme?.matchStyleDefaults || presetScheme?.styleDefaults;
  if (!styleDefaults || !matchDefaults) {
    return false;
  }

  return Object.entries(matchDefaults).every(([field, fieldValue]) =>
    classStyleSchemeFieldEqual(styleDefaults[field], fieldValue)
  );
};

/**
 * Resolve the human-readable scheme name for a class's saved style, and gallery routing metadata.
 * Custom schemes are checked before presets (same priority as the style editor).
 *
 * Pass `{ user, classItem }` so custom schemes match after the same `normalizeClassStyleDefaults`
 * merge as the class (legacy top-level clock/background fields + `classStyleDefaults` blob).
 * Custom list entries may include `styleDefaultsRaw` (Firebase shape) alongside normalized `styleDefaults`.
 */
export const resolveClassStyleSchemeSummary = (
  styleDefaults,
  customSchemes = [],
  options = {}
) => {
  const { user: optionsUser, classItem = null } = options || {};
  const user = optionsUser || {};
  const schemes = Array.isArray(customSchemes) ? customSchemes : [];

  let customMatch = null;
  const styleDefaultsForIds = classItem ? normalizeClassStyleDefaults(classItem, user) : styleDefaults;
  const customIdFromStorage = resolveAppliedClassStyleCustomSchemeId(styleDefaultsForIds, schemes);
  if (customIdFromStorage) {
    customMatch = schemes.find((schemeItem) => schemeItem.id === customIdFromStorage) || null;
  }

  if (!customMatch) {
    if (classItem) {
      const normalizedClass = normalizeClassStyleDefaults(classItem, user);
      customMatch = schemes.find((schemeItem) => {
        const raw = schemeItem.styleDefaultsRaw;
        if (raw == null) {
          return doesStyleMatchPresetScheme(styleDefaults, schemeItem);
        }
        const candidate = normalizeClassStyleDefaults({ ...classItem, classStyleDefaults: raw }, user);
        return classStyleNormalizedMapsEqual(normalizedClass, candidate);
      });
    } else {
      customMatch = schemes.find((schemeItem) => doesStyleMatchPresetScheme(styleDefaults, schemeItem));
    }
  }

  if (customMatch) {
    const label = String(customMatch.name || customMatch.label || "Untitled Style").trim() || "Untitled Style";
    return {
      label,
      schemeTab: "custom",
      matchedCustomId: customMatch.id,
      matchedPresetId: null,
    };
  }

  const styleDefaultsForPresets = classItem ? normalizeClassStyleDefaults(classItem, user) : styleDefaults;
  const presetIdFromStorage = resolveAppliedClassStylePresetId(styleDefaultsForPresets);
  const presetMatch = presetIdFromStorage
    ? getClassStylePresetSchemeById(presetIdFromStorage)
    : CLASS_STYLE_PRESET_SCHEMES.find((presetScheme) =>
        doesStyleMatchPresetScheme(styleDefaultsForPresets, presetScheme)
      );
  if (presetMatch) {
    return {
      label: presetMatch.label,
      schemeTab: "preset",
      matchedCustomId: null,
      matchedPresetId: presetMatch.id,
    };
  }

  return {
    label: "Customized style",
    schemeTab: "preset",
    matchedCustomId: null,
    matchedPresetId: null,
  };
};

const createClassStyleUsageEntry = (classItem = {}, index = 0) => ({
  classId: String(classItem?.classId || ""),
  className: String(classItem?.className || classItem?.name || "Class").trim() || "Class",
  period: String(classItem?.period || "").trim(),
  periodLabel: formatSidebarPeriodLabel(classItem?.period, index),
  labelColor: classItem?.labelColor || "",
});

const appendClassStyleUsageEntry = (usageMap, schemeId, usageEntry) => {
  if (!schemeId) {
    return;
  }

  if (!usageMap[schemeId]) {
    usageMap[schemeId] = [];
  }
  usageMap[schemeId].push(usageEntry);
};

export const buildClassStyleSchemeUsageMap = (classes = [], customSchemes = [], user = {}) => {
  const presetUsage = {};
  const customUsage = {};
  const classList = Array.isArray(classes) ? classes : [];

  classList.forEach((classItem, index) => {
    const classId = String(classItem?.classId || "");
    if (!classId) {
      return;
    }

    const normalizedStyleDefaults = normalizeClassStyleDefaults(classItem, user);
    const summary = resolveClassStyleSchemeSummary(normalizedStyleDefaults, customSchemes, {
      classItem,
      user,
    });
    const usageEntry = createClassStyleUsageEntry(classItem, index);

    if (summary.matchedCustomId) {
      appendClassStyleUsageEntry(customUsage, summary.matchedCustomId, usageEntry);
      return;
    }

    appendClassStyleUsageEntry(presetUsage, summary.matchedPresetId, usageEntry);
  });

  return { presetUsage, customUsage };
};

export const buildClassStylePayload = (classItem = {}, userOrSettings = {}, nextStyleDefaults = {}, options = {}) => {
  const normalized = normalizeClassStyleDefaults(
    { ...classItem, classStyleDefaults: nextStyleDefaults },
    userOrSettings,
    options
  );

  return {
    classStyleDefaults: normalized,
    background: normalized.background,
    backgroundAnnouncements: normalized.backgroundAnnouncements,
    backgroundClock: normalized.backgroundClock,
    backgroundAnnouncementsOpacity: normalized.backgroundAnnouncementsOpacity,
    backgroundClockOpacity: normalized.backgroundClockOpacity,
    clockFontColor: normalized.clockFontColor,
    clockLanguage: normalized.clockLanguage,
    clockFormat: normalized.clockFormat,
  };
};

export const buildCustomSchemeDeleteFallbackUpdates = ({
  classes = [],
  schemeId,
  user = {},
} = {}) => {
  const targetId = String(schemeId || "").trim();
  if (!targetId) {
    return [];
  }

  return (Array.isArray(classes) ? classes : [])
    .filter((classItem) => classItem?.classId && classItem.localMockSampleClass !== true)
    .filter((classItem) => classReferencesCustomStyleScheme(classItem, targetId, user))
    .map((classItem) => {
      const styleDefaults = buildDefaultPresetClassStyleDefaults(user, classItem);
      return {
        classId: classItem.classId,
        classItem,
        payload: buildClassStylePayload(classItem, user, styleDefaults, {
          preserveLegacyImageFallback: false,
        }),
      };
    });
};

const compactPlainObject = (value = {}) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined || entryValue === null || entryValue === "") {
      return accumulator;
    }
    if (typeof entryValue === "object" && !Array.isArray(entryValue)) {
      const compacted = compactPlainObject(entryValue);
      if (Object.keys(compacted).length > 0) {
        accumulator[key] = compacted;
      }
      return accumulator;
    }
    accumulator[key] = entryValue;
    return accumulator;
  }, {});
};

export const normalizeStyleOverrides = (styleOverrides = {}) => {
  const normalized = {
    background: compactPlainObject(styleOverrides?.background),
    typography: compactPlainObject(styleOverrides?.typography),
    surfaceChrome: compactPlainObject(styleOverrides?.surfaceChrome),
    agenda: compactPlainObject(styleOverrides?.agenda),
  };

  return compactPlainObject(normalized);
};

export const buildStyleOverridesWithSection = (styleOverrides = {}, section, sectionValue = {}) => {
  const nextOverrides = {
    ...normalizeStyleOverrides(styleOverrides),
    [section]: compactPlainObject(sectionValue),
  };

  if (Object.keys(nextOverrides[section] || {}).length === 0) {
    delete nextOverrides[section];
  }

  return normalizeStyleOverrides(nextOverrides);
};

export const resetStyleOverrideSection = (styleOverrides = {}, section) => {
  const nextOverrides = normalizeStyleOverrides(styleOverrides);
  delete nextOverrides[section];
  return normalizeStyleOverrides(nextOverrides);
};

export const clearStyleOverrides = () => ({});

export const getLegacyContentBoxBackgroundOverride = (content = {}) => {
  const background = content?.background;
  if (!background || background === "class-default") {
    return null;
  }
  return background;
};

export const getContentBoxBackgroundValue = (content = {}, classStyleDefaults = {}) =>
  pickFirstDefined(
    content?.styleOverrides?.background?.value,
    getLegacyContentBoxBackgroundOverride(content),
    classStyleDefaults?.cardBackgroundColor
  );

export const getContentBoxBackgroundOpacityValue = (content = {}, classStyleDefaults = {}) =>
  normalizeCardBackgroundOpacity(
    pickFirstDefined(
      content?.styleOverrides?.background?.opacity,
      classStyleDefaults?.cardBackgroundOpacity,
      1
    )
  );

export const getBackgroundPaintStyle = (backgroundValue, opacity = 1) => {
  if (!backgroundValue || backgroundValue === "class-default") {
    return {};
  }
  if (backgroundValue === "transparent") {
    return { backgroundColor: "transparent", backgroundImage: "none" };
  }
  if (isImageBackgroundValue(backgroundValue)) {
    const backgroundImage = backgroundValue.startsWith("url(") ? backgroundValue : `url(${backgroundValue})`;
    if (isPatternBackgroundValue(backgroundValue)) {
      return {
        backgroundImage,
        backgroundColor: "transparent",
      };
    }

    return {
      backgroundImage,
      backgroundColor: "transparent",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }
  if (isClassStyleCardBackgroundSolidHex(backgroundValue)) {
    const safeOpacity = normalizeSurfaceBackgroundOpacity(opacity);
    if (safeOpacity < 0.999) {
      const { r, g, b } = hexToRgb(backgroundValue);
      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, ${safeOpacity})`,
        backgroundImage: "none",
      };
    }
  }
  if (isGradientBackground(backgroundValue)) {
    return {
      background: backgroundValue,
      backgroundImage: "none",
    };
  }
  return {
    backgroundColor: backgroundValue,
    backgroundImage: "none",
  };
};

export const getStyleOverrideTypographyStyle = (styleOverrides = {}) => {
  const typography = normalizeStyleOverrides(styleOverrides).typography || {};
  return compactPlainObject({
    color: typography.color,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle,
  });
};

export const getSurfaceChromeFromOverrideSection = (surfaceChrome = {}, fallbackStyleDefaults = {}, surfaceId = "content") => {
  const overrideDefaults = {
    ...fallbackStyleDefaults,
  };
  const perSurfaceKeys = getPerSurfaceClassStyleFieldKeys(surfaceId);

  if (surfaceChrome.surfaceShadow !== undefined) {
    overrideDefaults[perSurfaceKeys.shadow] = surfaceChrome.surfaceShadow;
  }
  if (surfaceChrome.surfaceShadowCustom !== undefined) {
    overrideDefaults[perSurfaceKeys.shadowCustom] = surfaceChrome.surfaceShadowCustom;
  }
  if (surfaceChrome.surfaceOutlineWidth !== undefined) {
    overrideDefaults[perSurfaceKeys.outlineWidth] = surfaceChrome.surfaceOutlineWidth;
  }
  if (surfaceChrome.surfaceOutlineColor !== undefined) {
    overrideDefaults[perSurfaceKeys.outlineColor] = surfaceChrome.surfaceOutlineColor;
  }
  if (surfaceChrome.surfaceOutlineStyle !== undefined) {
    overrideDefaults[perSurfaceKeys.outlineStyle] = surfaceChrome.surfaceOutlineStyle;
  }
  if (surfaceChrome.surfaceCornerRadius !== undefined) {
    overrideDefaults[perSurfaceKeys.cornerRadius] = surfaceChrome.surfaceCornerRadius;
  }

  return {
    chrome: getClassStyleSurfaceChromeFor(overrideDefaults, surfaceId),
    boxShadow: getClassStyleShadowForSurface(overrideDefaults, surfaceId),
  };
};

export const resolveContentBoxAppearanceStyle = (content = {}, classStyleDefaults = {}, options = {}) => {
  if (options.isPrint) {
    return {};
  }

  const contentStyleDefaults = getClassStyleDefaultsForContentBoxPreset(
    classStyleDefaults,
    content?.contentBoxStylePresetId
  );
  const overrides = normalizeStyleOverrides(content?.styleOverrides);
  const backgroundValue = getContentBoxBackgroundValue(content, contentStyleDefaults);
  const backgroundOpacity = getContentBoxBackgroundOpacityValue(content, contentStyleDefaults);
  const backgroundStyle = backgroundValue
    ? getBackgroundPaintStyle(backgroundValue, backgroundOpacity)
    : resolveClassStyleCardBackgroundStyle(contentStyleDefaults);
  const surface = getSurfaceChromeFromOverrideSection(overrides.surfaceChrome, contentStyleDefaults, "content");
  const suppressInsetRim = Boolean(
    contentStyleDefaults.disableSurfaceInsetRim || classStyleDefaults.disableSurfaceInsetRim
  );
  const insetRim = "inset 0 0 0 1px rgba(15, 23, 42, 0.08)";
  const boxShadow = surface.boxShadow === "none"
    ? (suppressInsetRim ? "none" : insetRim)
    : (suppressInsetRim ? surface.boxShadow : `${surface.boxShadow}, ${insetRim}`);

  return {
    ...backgroundStyle,
    ...getStyleOverrideTypographyStyle(overrides),
    boxShadow,
    ...surface.chrome,
  };
};

export const AUTO_MUSIC_PLAYER_PRESET_ID = CONTENT_BOX_STYLE_PRESET_IDS[0];

export const resolveAutoMusicPlayerAppearanceStyle = (classStyleDefaults = {}) =>
  resolveContentBoxAppearanceStyle(
    { contentBoxStylePresetId: AUTO_MUSIC_PLAYER_PRESET_ID },
    classStyleDefaults
  );

const pickAutoMusicShellBackground = (appearance = {}) =>
  appearance.background ||
  appearance.backgroundColor ||
  appearance.backgroundImage ||
  undefined;

export const buildAutoMusicPlayerCssVariables = (classStyleDefaults = {}, appearance = null) => {
  const resolvedAppearance = appearance || resolveAutoMusicPlayerAppearanceStyle(classStyleDefaults);
  const presetDefaults = getClassStyleDefaultsForContentBoxPreset(
    classStyleDefaults,
    AUTO_MUSIC_PLAYER_PRESET_ID
  );
  const textColor = resolvedAppearance.color || presetDefaults.defaultFontColor || "#4c4c4c";
  const fontFamily = resolvedAppearance.fontFamily || presetDefaults.defaultFontStyle || "inherit";
  const shellBackground =
    pickAutoMusicShellBackground(resolvedAppearance) ||
    presetDefaults.cardBackgroundColor ||
    "#ffffff";
  const buttonBackground =
    resolvedAppearance.backgroundColor ||
    presetDefaults.cardBackgroundColor ||
    "#ffffff";
  const border = resolvedAppearance.border || "1px solid #e2e8f0";
  const borderRadius = resolvedAppearance.borderRadius || "8px";
  const boxShadow =
    resolvedAppearance.boxShadow ||
    "0 0 15px rgba(0, 0, 0, 0.15), 0 0 1px 1px rgba(0, 0, 0, 0.1)";

  return {
    "--auto-music-background": shellBackground,
    "--auto-music-color": textColor,
    "--auto-music-font-family": fontFamily,
    "--auto-music-border": border,
    "--auto-music-border-radius": borderRadius,
    "--auto-music-box-shadow": boxShadow,
    "--auto-music-button-bg": buttonBackground,
    "--auto-music-button-color": textColor,
    "--auto-music-button-border": border !== "none" ? border : "2px solid currentColor",
    "--auto-music-header-background": buttonBackground,
    "--auto-music-header-color": textColor,
  };
};

export const MUSIC_TOOLS_CONTENT_PRESET_ID = CONTENT_BOX_STYLE_PRESET_IDS[0];

export const resolveMusicToolsAppearanceStyle = (classStyleDefaults = {}) =>
  resolveContentBoxAppearanceStyle(
    { contentBoxStylePresetId: MUSIC_TOOLS_CONTENT_PRESET_ID },
    classStyleDefaults
  );

/**
 * CSS variables for music-tools embeds that opt into Present class styling
 * via `?classStyles=1`.
 */
export const buildMusicToolsCssVariables = (classStyleDefaults = {}, appearance = null) => {
  const resolvedAppearance = appearance || resolveMusicToolsAppearanceStyle(classStyleDefaults);
  const presetDefaults = getClassStyleDefaultsForContentBoxPreset(
    classStyleDefaults,
    MUSIC_TOOLS_CONTENT_PRESET_ID
  );
  const textColor = resolvedAppearance.color || presetDefaults.defaultFontColor || "#334155";
  const fontFamily = resolvedAppearance.fontFamily || presetDefaults.defaultFontStyle || "inherit";
  const shellBackground =
    pickAutoMusicShellBackground(resolvedAppearance) ||
    presetDefaults.cardBackgroundColor ||
    "#ffffff";
  const border = resolvedAppearance.border || "1px solid rgba(0, 74, 173, 0.14)";
  const borderRadius = resolvedAppearance.borderRadius || "18px";
  const boxShadow =
    resolvedAppearance.boxShadow
    && resolvedAppearance.boxShadow !== "none"
      ? resolvedAppearance.boxShadow
      : "0 10px 30px rgba(15, 23, 42, 0.1)";
  const headerBackground =
    classStyleDefaults.agendaActiveStepBackgroundColor || "#004aad";
  const headerColor =
    classStyleDefaults.agendaActiveStepColor || "#ffffff";
  const softBackground =
    classStyleDefaults.reminderBackgroundColor
    || classStyleDefaults.agendaBackgroundColor
    || shellBackground;
  const mutedColor =
    classStyleDefaults.agendaSubStepColor || textColor;

  return {
    "--mt-widget-bg": shellBackground,
    "--mt-widget-border": border,
    "--mt-widget-radius": borderRadius,
    "--mt-widget-shadow": boxShadow,
    "--mt-header-bg": headerBackground,
    "--mt-header-color": headerColor,
    "--mt-body-bg": softBackground,
    "--mt-body-color": textColor,
    "--mt-muted-color": mutedColor,
    "--mt-font-family": fontFamily,
    "--mt-accent": headerBackground,
    "--mt-accent-text": headerColor,
    "--mt-button-bg": headerBackground,
    "--mt-button-color": headerColor,
    "--mt-chip-active-border": headerBackground,
    "--mt-chip-active-bg": softBackground,
    "--mt-chip-active-color": headerBackground,
    "--mt-range-bright": headerBackground,
    "--mt-range-mid": headerBackground,
    "--mt-range-deep": headerBackground,
  };
};

export const getAgendaBoxBackgroundValue = (content = {}, classStyleDefaults = {}) =>
  pickFirstDefined(
    content?.styleOverrides?.background?.value,
    getLegacyContentBoxBackgroundOverride(content),
    classStyleDefaults?.agendaBackgroundColor
  );

export const getAgendaBoxStyleDraft = (content = {}) => {
  const existingOverrides = normalizeStyleOverrides(content?.styleOverrides);
  const legacyAgenda = compactPlainObject({
    titleFont: content?.titleFont,
    titleColor: content?.titleColor,
    stepFont: content?.stepFont,
    stepColor: content?.stepColor,
    subTaskStepFont: content?.subTaskStepFont,
    subTaskStepColor: content?.subTaskStepColor,
  });

  const nextDraft = {
    ...existingOverrides,
    agenda: compactPlainObject({
      ...legacyAgenda,
      ...(existingOverrides.agenda || {}),
    }),
  };

  if (!nextDraft?.background?.value && content?.background && content.background !== "class-default") {
    nextDraft.background = {
      ...(nextDraft.background || {}),
      value: content.background,
    };
  }

  return normalizeStyleOverrides(nextDraft);
};

export const hasAgendaBoxStyleOverrides = (content = {}) =>
  Object.keys(getAgendaBoxStyleDraft(content)).length > 0;

export const buildAgendaBoxStyleOverridesPayload = (content = {}, nextOverrides = {}) => {
  const normalized = normalizeStyleOverrides(nextOverrides);
  const agenda = normalized.agenda || {};
  const styleOverrides = normalized;
  const valueOrNull = (value) => (value === undefined || value === "" ? null : value);

  return {
    styleOverrides,
    background: normalized?.background?.value || "class-default",
    titleFont: valueOrNull(agenda.titleFont),
    titleColor: valueOrNull(agenda.titleColor),
    stepFont: valueOrNull(agenda.stepFont),
    stepColor: valueOrNull(agenda.stepColor),
    subTaskStepFont: valueOrNull(agenda.subTaskStepFont),
    subTaskStepColor: valueOrNull(agenda.subTaskStepColor),
  };
};

export const resolveAgendaBoxAppearanceStyle = (content = {}, classStyleDefaults = {}, options = {}) => {
  if (options.isPrint) {
    return {
      cardStyle: {},
      titleStyle: {},
      stepStyle: {},
      subStepStyle: {},
      activeStepStyle: {},
      activeSubStepStyle: {},
    };
  }

  const overrides = normalizeStyleOverrides(content?.styleOverrides);
  const agendaOverrides = overrides.agenda || {};
  const backgroundValue = getAgendaBoxBackgroundValue(content, classStyleDefaults);
  const backgroundOpacity = normalizeCardBackgroundOpacity(
    pickFirstDefined(overrides?.background?.opacity, classStyleDefaults.agendaBackgroundOpacity)
  );
  const backgroundStyle = backgroundValue
    ? getBackgroundPaintStyle(backgroundValue, backgroundOpacity)
    : getBackgroundPaintStyle(classStyleDefaults.agendaBackgroundColor, backgroundOpacity);
  const surface = getSurfaceChromeFromOverrideSection(overrides.surfaceChrome, classStyleDefaults, "agenda");

  return {
    cardStyle: {
      ...backgroundStyle,
      boxShadow: surface.boxShadow === "none"
        ? "inset 0 0 0 1px rgba(15, 23, 42, 0.08)"
        : `${surface.boxShadow}, inset 0 0 0 1px rgba(15, 23, 42, 0.08)`,
      ...surface.chrome,
    },
    titleStyle: compactPlainObject({
      fontFamily: pickFirstDefined(agendaOverrides.titleFont, content?.titleFont, classStyleDefaults.agendaTitleFont),
      color: pickFirstDefined(agendaOverrides.titleColor, content?.titleColor, classStyleDefaults.agendaTitleColor),
    }),
    stepStyle: compactPlainObject({
      fontFamily: pickFirstDefined(agendaOverrides.stepFont, content?.stepFont, classStyleDefaults.agendaStepFont),
      color: pickFirstDefined(agendaOverrides.stepColor, content?.stepColor, classStyleDefaults.agendaStepColor),
    }),
    subStepStyle: compactPlainObject({
      fontFamily: pickFirstDefined(agendaOverrides.subTaskStepFont, content?.subTaskStepFont, classStyleDefaults.agendaSubStepFont),
      color: pickFirstDefined(agendaOverrides.subTaskStepColor, content?.subTaskStepColor, classStyleDefaults.agendaSubStepColor),
    }),
    activeStepStyle: compactPlainObject({
      backgroundColor: pickFirstDefined(agendaOverrides.activeStepBackgroundColor, classStyleDefaults.agendaActiveStepBackgroundColor),
      color: pickFirstDefined(agendaOverrides.activeStepColor, classStyleDefaults.agendaActiveStepColor),
    }),
    activeSubStepStyle: compactPlainObject({
      color: pickFirstDefined(agendaOverrides.activeSubStepColor, classStyleDefaults.agendaActiveSubStepColor),
    }),
    progressStyle: compactPlainObject({
      trackColor: pickFirstDefined(agendaOverrides.progressTrackColor, classStyleDefaults.agendaProgressTrackColor),
      fillColor: pickFirstDefined(agendaOverrides.progressFillColor, classStyleDefaults.agendaProgressFillColor),
      scrubberColor: pickFirstDefined(agendaOverrides.progressScrubberColor, classStyleDefaults.agendaProgressScrubberColor),
    }),
  };
};

export const buildContentBoxStyleOverridesPayload = (content = {}, nextOverrides = {}) => {
  const normalized = normalizeStyleOverrides(nextOverrides);
  return {
    styleOverrides: normalized,
    background: normalized?.background?.value || "class-default",
  };
};

export const getClassStyleEditorSettings = (userSettings = {}, classItem = {}, options = {}) => {
  const styleDefaults = normalizeClassStyleDefaults(classItem, userSettings);
  // When editing a content box, prefer that box's style preset. Root defaultFont*
  // fields are shared with the marquee and can disagree with box presets.
  const presetDefaults = options.contentBoxStylePresetId
    ? getClassStyleDefaultsForContentBoxPreset(styleDefaults, options.contentBoxStylePresetId)
    : styleDefaults;

  return {
    ...userSettings,
    defaultFontStyle: presetDefaults.defaultFontStyle,
    defaultFontSize: presetDefaults.defaultFontSize,
    defaultFontColor: presetDefaults.defaultFontColor,
  };
};

